const ESENSERA_AUTO_SELECT_FACET_KINDS = new Set([
  "type",
  "scent_profile",
  "aromatherapy",
  "organic",
  /*
   * En enkelt Olfactive character må ikke automatisk erstatte en
   * produktsøgning. Et ord som "rose" kan både være et produktnavn
   * og et tag. Forslaget vises stadig, men titelmatch får lov at vinde.
   */
]);

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.abortController = null;
    this.requestId = 0;
    this.searchMap = null;
    this.contentIndex = null;
    this.latestSearchOutcome = null;
    this.catalogProductResponsePromise = null;

    this.minimumQueryLength = 3;
    this.maximumMappedSuggestions = 4;
    this.maximumPredictiveProducts = 10;
    this.maximumPredictivePages = 4;
    this.maximumPredictiveArticles = 4;

    this.init();
  }

  init() {
    this.target = this.closest("action-search");

    if (!this.target) return;

    this.input = this.target.querySelector('input[type="search"]');
    this.form =
      this.target.querySelector('form[role="search"]') ||
      this.target.querySelector("form");
    this.predictiveSearchResults =
      this.target.querySelector(".predictive-search");
    this.searchKeyword = this.target.querySelector(".search-keyword");
    this.recommendSearch = this.target.querySelector(".recommend-search");

    if (!this.input || !this.form || !this.predictiveSearchResults) {
      return;
    }

    this.searchMap = this.readJsonScript(
      "[data-esensera-search-map]",
      "ESENSERA search map"
    );

    this.contentIndex = this.readJsonScript(
      "[data-esensera-search-content-index]",
      "ESENSERA content index"
    );

    this.input.addEventListener(
      "input",
      this.debounce(() => {
        this.onInput();
      }, 300)
    );

    this.form.addEventListener("submit", (event) => {
      this.onSubmit(event);
    });
  }

  readJsonScript(selector, label) {
    const element = this.target.querySelector(selector);

    if (!element) {
      console.warn(`${label} blev ikke fundet.`);
      return null;
    }

    try {
      return JSON.parse(element.textContent.trim());
    } catch (error) {
      console.error(`${label} kunne ikke læses:`, error);
      return null;
    }
  }

  normalizeTerm(value) {
    return String(value || "")
      .normalize("NFC")
      .toLocaleLowerCase("da-DK")
      .trim()
      .replace(/\s+/g, " ");
  }

  normalizeFuzzyTerm(value) {
    return this.normalizeTerm(value)
      .replace(/æ/g, "ae")
      .replace(/ø/g, "o")
      .replace(/å/g, "a")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .replace(/\s+/g, " ")
      .trim();
  }


  tokenizeQuery(value) {
    const normalizedValue = String(value || "").normalize("NFC");
    const matches = [...normalizedValue.matchAll(/[\p{L}\p{N}]+/gu)];

    return matches
      .map((match, index) => ({
        text: this.normalizeTerm(match[0]),
        consumed: false,
        index,
        start: Number(match.index || 0),
        end: Number(match.index || 0) + match[0].length
      }))
      .filter((token) => Boolean(token.text));
  }

  normalizeIntentToken(value) {
    return this.normalizeTerm(value)
      .replace(/[^\p{L}\p{N}]/gu, "")
      .trim();
  }

  getIntentLanguageConfig() {
    return this.searchMap?.intent_language || {};
  }

  getComposableIntentEntries() {
    if (!Array.isArray(this.searchMap?.entries)) {
      return [];
    }

    return this.searchMap.entries.filter((entry) => {
      return (
        entry?.composable_intent === true &&
        Boolean(entry?.filter_param) &&
        String(entry?.filter_value || "").trim() !== ""
      );
    });
  }

  getOlfactiveIntentEntries() {
    if (!Array.isArray(this.searchMap?.entries)) {
      return [];
    }

    return this.searchMap.entries.filter((entry) => {
      return (
        entry?.kind === "olfactive_character" &&
        Boolean(entry?.filter_param) &&
        String(entry?.filter_value || "").trim() !== "" &&
        Array.isArray(entry?.terms)
      );
    });
  }

  getEntryComposableTerms(entry) {
    const terms = Array.isArray(entry?.composable_terms)
      ? entry.composable_terms
      : entry?.terms;

    return Array.isArray(terms) ? terms : [];
  }

  getEntryKey(entry) {
    const id = String(entry?.id || "").trim();

    if (id) {
      return `id:${id}`;
    }

    return [
      String(entry?.filter_param || "").trim(),
      this.normalizeTerm(entry?.filter_value)
    ].join("\u0000");
  }

  addUniqueEntry(entries, entry) {
    if (!entry) {
      return;
    }

    const key = this.getEntryKey(entry);

    if (!key || entries.some((candidate) => this.getEntryKey(candidate) === key)) {
      return;
    }

    entries.push(entry);
  }

  getUniqueEntries(entries) {
    const uniqueEntries = [];

    (Array.isArray(entries) ? entries : []).forEach((entry) => {
      this.addUniqueEntry(uniqueEntries, entry);
    });

    return uniqueEntries;
  }

  getRemainingSearchTerm(tokens) {
    return (Array.isArray(tokens) ? tokens : [])
      .filter((token) => !token.consumed && token.text)
      .map((token) => token.text)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }

  isAllowedTrailingIntentPrefix(
    entry,
    comparableToken,
    spanLength,
    rawToken = ""
  ) {
    const normalizedRawToken = this.normalizeTerm(rawToken);

    /*
     * Et enkelt dansk "ø" efter et etableret produktnavn er allerede et
     * entydigt begyndende øko-signal. Bevar derfor produktresultatet fra
     * første bogstav i "øko", mens et almindeligt engelsk "o" fortsat er
     * for tvetydigt til at blive fortolket som "organic".
     */
    if (
      entry?.kind === "organic" &&
      spanLength === 1 &&
      normalizedRawToken === "ø"
    ) {
      return true;
    }

    const minimumLength = spanLength > 1 ? 1 : 2;

    if (!comparableToken || comparableToken.length < minimumLength) {
      return false;
    }

    if (entry?.kind !== "organic") {
      return true;
    }

    /*
     * Undgå at et kort, almindeligt produktord som "or..." midlertidigt
     * bliver læst som "organic". De danske øko-varianter er tydelige fra
     * to tegn, mens den engelske rod først er sikker fra "org".
     */
    return (
      comparableToken.startsWith("ok") ||
      comparableToken.startsWith("oek") ||
      comparableToken.startsWith("org")
    );
  }

  consumeTrailingProductIntentPrefixes(tokens, entries, matchedEntries) {
    if (!Array.isArray(tokens) || tokens.length < 2) {
      return false;
    }

    const candidates = [];

    entries
      .filter((entry) => ["type", "organic"].includes(entry?.kind))
      .forEach((entry) => {
        this.getEntryComposableTerms(entry).forEach((term) => {
          const termTokens = this.tokenizeQuery(term)
            .map((token) => this.normalizeFuzzyTerm(token.text))
            .filter(Boolean);

          if (!termTokens.length) {
            return;
          }

          for (let startIndex = 1; startIndex < tokens.length; startIndex += 1) {
            const span = tokens.slice(startIndex);

            if (
              span.length > termTokens.length ||
              span.some((token) => token.consumed) ||
              !tokens
                .slice(0, startIndex)
                .some((token) => !token.consumed && token.text)
            ) {
              continue;
            }

            const comparableSpan = span.map((token) =>
              this.normalizeFuzzyTerm(token.text)
            );

            const lastComparableToken =
              comparableSpan[comparableSpan.length - 1] || "";

            /*
             * Et færdigskrevet, men alene stående første ord i et længere
             * typeudtryk er ikke i sig selv en sikker typeintention.
             *
             * Eksempel: "agermynte olie" må ikke blive læst som begyndelsen
             * på typen "olie blanding". Når kunden faktisk fortsætter med
             * "olie bl...", bliver præfikset stadig genkendt korrekt.
             */
            const expectedLastToken =
              termTokens[comparableSpan.length - 1] || "";

            const isBareGenericProductWord =
              comparableSpan.length === 1 &&
              this.getGenericProductWords().has(lastComparableToken) &&
              (termTokens.length > 1 ||
                expectedLastToken !== lastComparableToken);

            if (
              isBareGenericProductWord ||
              comparableSpan.length < termTokens.length &&
              lastComparableToken === expectedLastToken
            ) {
              continue;
            }

            if (
              !this.isAllowedTrailingIntentPrefix(
                entry,
                lastComparableToken,
                span.length,
                span[span.length - 1]?.text || ""
              )
            ) {
              continue;
            }

            const isMatchingPrefix = comparableSpan.every(
              (comparableToken, offset) => {
                const expectedToken = termTokens[offset];

                if (!expectedToken || !comparableToken) {
                  return false;
                }

                if (offset === comparableSpan.length - 1) {
                  return expectedToken.startsWith(comparableToken);
                }

                return expectedToken === comparableToken;
              }
            );

            if (!isMatchingPrefix) {
              continue;
            }

            candidates.push({
              entry,
              startIndex,
              spanLength: span.length,
              termLength: termTokens.join("").length,
              matchedLength: comparableSpan.join("").length
            });
          }
        });
      });

    const bestCandidate = candidates.sort((candidateA, candidateB) => {
      return (
        candidateB.spanLength - candidateA.spanLength ||
        candidateB.matchedLength - candidateA.matchedLength ||
        candidateA.termLength - candidateB.termLength ||
        Number(candidateB.entry?.priority || 0) -
          Number(candidateA.entry?.priority || 0)
      );
    })[0];

    if (!bestCandidate) {
      return false;
    }

    tokens.slice(bestCandidate.startIndex).forEach((token) => {
      token.consumed = true;
    });

    this.addUniqueEntry(matchedEntries, bestCandidate.entry);
    return true;
  }

  applyCompoundIntentSuffixes(tokens, entries, matchedEntries) {
    let changed = false;

    entries.forEach((entry) => {
      const suffixes = Array.isArray(entry?.compound_suffixes)
        ? entry.compound_suffixes
            .map((suffix) => this.normalizeIntentToken(suffix))
            .filter(Boolean)
            .sort((suffixA, suffixB) => suffixB.length - suffixA.length)
        : [];

      if (!suffixes.length) {
        return;
      }

      const exclusions = new Set(
        (Array.isArray(entry?.compound_exclusions)
          ? entry.compound_exclusions
          : []
        )
          .map((value) => this.normalizeIntentToken(value))
          .filter(Boolean)
      );

      const minimumBaseLength = Math.max(
        1,
        Number(entry?.compound_min_base_length || 3)
      );

      tokens.forEach((token) => {
        if (token.consumed) {
          return;
        }

        const comparableToken = this.normalizeIntentToken(token.text);

        if (!comparableToken || exclusions.has(comparableToken)) {
          return;
        }

        const matchingSuffix = suffixes.find((suffix) => {
          if (comparableToken === suffix) {
            return true;
          }

          if (!comparableToken.endsWith(suffix)) {
            return false;
          }

          return comparableToken.length - suffix.length >= minimumBaseLength;
        });

        if (!matchingSuffix) {
          return;
        }

        this.addUniqueEntry(matchedEntries, entry);
        changed = true;

        if (comparableToken === matchingSuffix) {
          token.consumed = true;
          return;
        }

        token.text = comparableToken.slice(0, -matchingSuffix.length);
        token.consumed = !token.text;
      });
    });

    return changed;
  }

  consumeExactIntentTerms(tokens, entries, matchedEntries) {
    let changed = false;

    const candidates = entries
      .flatMap((entry) => {
        return this.getEntryComposableTerms(entry).map((term) => {
          const termTokens = this.tokenizeQuery(term)
            .map((token) => this.normalizeFuzzyTerm(token.text))
            .filter(Boolean);

          return {
            entry,
            termTokens,
            termLength: termTokens.join("").length
          };
        });
      })
      .filter((candidate) => candidate.termTokens.length)
      .sort((candidateA, candidateB) => {
        return (
          candidateB.termTokens.length - candidateA.termTokens.length ||
          candidateB.termLength - candidateA.termLength ||
          Number(candidateB.entry?.priority || 0) -
            Number(candidateA.entry?.priority || 0)
        );
      });

    const exactPhraseEntryKeys = new Map();

    candidates.forEach(({ entry, termTokens }) => {
      const phraseKey = termTokens.join("\u0000");
      const entryKey = this.getEntryKey(entry);

      if (!exactPhraseEntryKeys.has(phraseKey)) {
        exactPhraseEntryKeys.set(phraseKey, new Set());
      }

      exactPhraseEntryKeys.get(phraseKey).add(entryKey);
    });

    candidates.forEach(({ entry, termTokens }) => {
      const phraseKey = termTokens.join("\u0000");

      /*
       * Et generisk ord som fx "ekstrakt" kan med vilje tilhøre flere
       * strukturerede kategorier. I den situation må vi ikke arbitrært
       * konsumere ordet som den første facet og dermed låse søgningen.
       * Kun entydige, fulde composable-termer må blive til intent.
       */
      if ((exactPhraseEntryKeys.get(phraseKey)?.size || 0) !== 1) {
        return;
      }

      const tokenCount = termTokens.length;

      for (
        let startIndex = 0;
        startIndex <= tokens.length - tokenCount;
        startIndex += 1
      ) {
        const span = tokens.slice(startIndex, startIndex + tokenCount);

        if (span.some((token) => token.consumed)) {
          continue;
        }

        const isMatch = span.every((token, offset) => {
          return this.normalizeFuzzyTerm(token.text) === termTokens[offset];
        });

        if (!isMatch) {
          continue;
        }

        span.forEach((token) => {
          token.consumed = true;
        });

        this.addUniqueEntry(matchedEntries, entry);
        changed = true;
      }
    });

    return changed;
  }

  consumeFuzzyIntentTerms(tokens, entries, matchedEntries) {
    let changed = false;

    entries
      .filter((entry) => entry?.fuzzy_match === true)
      .forEach((entry) => {
        const fuzzyTerms = Array.isArray(entry?.fuzzy_terms)
          ? entry.fuzzy_terms
          : entry?.terms;

        const maximumWordCount = Math.max(
          1,
          ...(Array.isArray(fuzzyTerms) ? fuzzyTerms : []).map((term) => {
            return Math.max(1, this.tokenizeQuery(term).length);
          })
        );

        for (
          let wordCount = maximumWordCount;
          wordCount >= 1;
          wordCount -= 1
        ) {
          for (
            let startIndex = 0;
            startIndex <= tokens.length - wordCount;
            startIndex += 1
          ) {
            const span = tokens.slice(startIndex, startIndex + wordCount);

            if (span.some((token) => token.consumed)) {
              continue;
            }

            const phrase = span.map((token) => token.text).join(" ");

            if (!this.isFuzzyEntryMatch(entry, phrase)) {
              continue;
            }

            span.forEach((token) => {
              token.consumed = true;
            });

            this.addUniqueEntry(matchedEntries, entry);
            changed = true;
          }
        }
      });

    return changed;
  }

  getOlfactiveTermCandidates() {
    const seen = new Set();

    return this.getOlfactiveIntentEntries()
      .flatMap((entry) => {
        return entry.terms.map((term) => {
          const termTokens = this.tokenizeQuery(term)
            .map((token) => this.normalizeFuzzyTerm(token.text))
            .filter(Boolean);

          return {
            entry,
            termTokens,
            termLength: termTokens.join("").length
          };
        });
      })
      .filter((candidate) => {
        if (!candidate.termTokens.length) {
          return false;
        }

        const key = `${this.getEntryKey(candidate.entry)}\u0000${candidate.termTokens.join(" ")}`;

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      })
      .sort((candidateA, candidateB) => {
        return (
          candidateB.termTokens.length - candidateA.termTokens.length ||
          candidateB.termLength - candidateA.termLength ||
          Number(candidateB.entry?.priority || 0) -
            Number(candidateA.entry?.priority || 0)
        );
      });
  }

  findOlfactiveTokenMatches(tokens) {
    const occupiedIndexes = new Set();
    const matches = [];

    this.getOlfactiveTermCandidates().forEach(({ entry, termTokens }) => {
      const tokenCount = termTokens.length;

      /*
       * Et enkelt Olfactive character-ord skal selv opfylde den globale
       * minimumslængde. Ellers bliver produktnavnet "Ho Wood" fejlagtigt
       * læst som kombinationen Hø + Woody, fordi ø normaliseres til o.
       */
      if (
        tokenCount === 1 &&
        Array.from(termTokens[0] || "").length < this.minimumQueryLength
      ) {
        return;
      }

      for (
        let startIndex = 0;
        startIndex <= tokens.length - tokenCount;
        startIndex += 1
      ) {
        const indexes = Array.from(
          { length: tokenCount },
          (_, offset) => startIndex + offset
        );

        if (
          indexes.some(
            (index) => tokens[index]?.consumed || occupiedIndexes.has(index)
          )
        ) {
          continue;
        }

        const isMatch = indexes.every((index, offset) => {
          return (
            this.normalizeFuzzyTerm(tokens[index]?.text) === termTokens[offset]
          );
        });

        if (!isMatch) {
          continue;
        }

        indexes.forEach((index) => occupiedIndexes.add(index));
        matches.push({ entry, indexes });
        break;
      }
    });

    return matches.sort((matchA, matchB) => {
      return Math.min(...matchA.indexes) - Math.min(...matchB.indexes);
    });
  }

  hasExplicitOlfactiveConnector(searchTerm, tokens, matches) {
    const rawTerm = String(searchTerm || "").normalize("NFC");
    const connectorWords = new Set(
      (Array.isArray(this.getIntentLanguageConfig()?.olfactive_connectors)
        ? this.getIntentLanguageConfig().olfactive_connectors
        : []
      )
        .map((value) => this.normalizeFuzzyTerm(value))
        .filter(Boolean)
    );

    const orderedMatches = [...matches].sort((matchA, matchB) => {
      return Math.min(...matchA.indexes) - Math.min(...matchB.indexes);
    });

    for (let index = 0; index < orderedMatches.length - 1; index += 1) {
      const leftMatch = orderedMatches[index];
      const rightMatch = orderedMatches[index + 1];
      const leftTokenIndex = Math.max(...leftMatch.indexes);
      const rightTokenIndex = Math.min(...rightMatch.indexes);
      const leftToken = tokens[leftTokenIndex];
      const rightToken = tokens[rightTokenIndex];

      const tokensBetween = tokens.slice(
        leftTokenIndex + 1,
        rightTokenIndex
      );

      if (
        tokensBetween.some((token) => {
          return (
            !token.consumed &&
            connectorWords.has(this.normalizeFuzzyTerm(token.text))
          );
        })
      ) {
        return true;
      }

      if (!leftToken || !rightToken) {
        continue;
      }

      const separator = rawTerm.slice(leftToken.end, rightToken.start);

      if (/[+&,/]/u.test(separator) || /-/u.test(separator)) {
        return true;
      }
    }

    return false;
  }

  consumeOlfactiveIntentTerms(tokens, matchedEntries, searchTerm) {
    const matches = this.findOlfactiveTokenMatches(tokens);
    const uniqueOlfactiveEntries = this.getUniqueEntries(
      matches.map((match) => match.entry)
    );

    if (uniqueOlfactiveEntries.length < 2) {
      return {
        changed: false,
        entries: []
      };
    }

    const matchedIndexes = new Set(
      matches.flatMap((match) => match.indexes)
    );

    const languageConfig = this.getIntentLanguageConfig();
    const connectorWords = new Set(
      (Array.isArray(languageConfig?.olfactive_connectors)
        ? languageConfig.olfactive_connectors
        : []
      )
        .map((value) => this.normalizeFuzzyTerm(value))
        .filter(Boolean)
    );

    const contextWords = new Set(
      (Array.isArray(languageConfig?.olfactive_context_words)
        ? languageConfig.olfactive_context_words
        : []
      )
        .map((value) => this.normalizeFuzzyTerm(value))
        .filter(Boolean)
    );

    const explicitConnector = this.hasExplicitOlfactiveConnector(
      searchTerm,
      tokens,
      matches
    );

    const remainingMeaningfulTokens = tokens.filter((token, index) => {
      if (token.consumed || matchedIndexes.has(index)) {
        return false;
      }

      const comparableToken = this.normalizeFuzzyTerm(token.text);

      return (
        comparableToken &&
        !connectorWords.has(comparableToken) &&
        !contextWords.has(comparableToken)
      );
    });

    /*
     * Uden et tydeligt bindeord eller tegn må hele søgningen være en
     * duftbeskrivelse. Dermed bliver "frisk frugtagtig" en præcis
     * kombination, mens "lavendel frisk frugtagtig" ikke overfortolkes.
     * Med +, &, /, komma, bindestreg, "og" eller "and" kan et produktnavn
     * godt stå sammen med to eller flere eksplicitte duftkarakterer.
     */
    if (!explicitConnector && remainingMeaningfulTokens.length) {
      return {
        changed: false,
        entries: []
      };
    }

    matches.forEach((match) => {
      match.indexes.forEach((index) => {
        if (tokens[index]) {
          tokens[index].consumed = true;
        }
      });

      this.addUniqueEntry(matchedEntries, match.entry);
    });

    tokens.forEach((token) => {
      if (token.consumed) {
        return;
      }

      const comparableToken = this.normalizeFuzzyTerm(token.text);

      if (
        connectorWords.has(comparableToken) ||
        contextWords.has(comparableToken)
      ) {
        token.consumed = true;
      }
    });

    return {
      changed: true,
      entries: uniqueOlfactiveEntries
    };
  }

  stripGenericProductDescriptors(tokens, facetEntries) {
    const languageConfig = this.getIntentLanguageConfig();
    const genericWords = new Set(
      (Array.isArray(languageConfig?.generic_product_words)
        ? languageConfig.generic_product_words
        : []
      )
        .map((value) => this.normalizeIntentToken(value))
        .filter(Boolean)
    );

    const suffixes = (
      Array.isArray(languageConfig?.generic_product_suffixes)
        ? languageConfig.generic_product_suffixes
        : []
    )
      .map((value) => this.normalizeIntentToken(value))
      .filter(Boolean)
      .sort((suffixA, suffixB) => suffixB.length - suffixA.length);

    const exclusions = new Set(
      (Array.isArray(languageConfig?.generic_product_suffix_exclusions)
        ? languageConfig.generic_product_suffix_exclusions
        : []
      )
        .map((value) => this.normalizeIntentToken(value))
        .filter(Boolean)
    );

    let changed = false;

    let trailingTokenIndex = -1;

    for (let index = tokens.length - 1; index >= 0; index -= 1) {
      if (!tokens[index].consumed && tokens[index].text) {
        trailingTokenIndex = index;
        break;
      }
    }

    if (trailingTokenIndex >= 0) {
      const trailingToken = tokens[trailingTokenIndex];
      const comparableTrailingToken = this.normalizeIntentToken(
        trailingToken.text
      );
      const hasEarlierProductTerm = tokens
        .slice(0, trailingTokenIndex)
        .some((token) => !token.consumed && token.text);
      const isGenericWordPrefix =
        comparableTrailingToken.length >= 2 &&
        !genericWords.has(comparableTrailingToken) &&
        [...genericWords].some((word) =>
          word.startsWith(comparableTrailingToken)
        );

      if (
        isGenericWordPrefix &&
        (hasEarlierProductTerm || facetEntries.length)
      ) {
        trailingToken.consumed = true;
        changed = true;
      }
    }

    tokens.forEach((token) => {
      if (token.consumed) {
        return;
      }

      const comparableToken = this.normalizeIntentToken(token.text);

      if (
        !comparableToken ||
        genericWords.has(comparableToken) ||
        exclusions.has(comparableToken)
      ) {
        return;
      }

      const matchingSuffix = suffixes.find((suffix) => {
        return (
          comparableToken.endsWith(suffix) &&
          comparableToken.length - suffix.length >= 3
        );
      });

      if (!matchingSuffix) {
        return;
      }

      token.text = comparableToken.slice(0, -matchingSuffix.length);
      token.consumed = !token.text;
      changed = true;
    });

    const unconsumedTokens = tokens.filter(
      (token) => !token.consumed && token.text
    );

    const hasNonGenericProductTerm = unconsumedTokens.some((token) => {
      return !genericWords.has(this.normalizeIntentToken(token.text));
    });

    if (hasNonGenericProductTerm || facetEntries.length) {
      unconsumedTokens.forEach((token) => {
        if (genericWords.has(this.normalizeIntentToken(token.text))) {
          token.consumed = true;
          changed = true;
        }
      });
    }

    return changed;
  }

  rewriteRemainingProductTerms(tokens) {
    const rewrites =
      this.getIntentLanguageConfig()?.product_term_rewrites_after_generic_word ||
      {};

    let changed = false;

    tokens.forEach((token) => {
      if (token.consumed || !token.text) {
        return;
      }

      const comparableToken = this.normalizeIntentToken(token.text);
      const replacement = this.normalizeTerm(rewrites[comparableToken]);

      if (!replacement || replacement === token.text) {
        return;
      }

      token.text = replacement;
      changed = true;
    });

    return changed;
  }

  classifyFacetEntries(entries, searchTerm) {
    const allIntentEntries = this.getUniqueEntries(entries);
    const hasProductTerm = Boolean(this.normalizeTerm(searchTerm));

    /*
     * En eksplicit type efter et produktnavn er en søgekvalifikator, ikke
     * et synligt storefront-filter. "Lavendel æterisk olie" skal derfor
     * kun vise Lavendel-produkter af den rigtige type, mens Type stadig
     * står på "Alle produkter". Rene typesøgninger beholder typefilteret.
     */
    const productQualifierEntries = hasProductTerm
      ? allIntentEntries.filter((entry) => entry?.kind === "type")
      : [];

    const qualifierKeys = new Set(
      productQualifierEntries.map((entry) => this.getEntryKey(entry))
    );

    const facetEntries = allIntentEntries.filter((entry) => {
      return !qualifierKeys.has(this.getEntryKey(entry));
    });

    const preferredFacetEntries = hasProductTerm
      ? facetEntries.filter((entry) => entry?.soft_with_product_query === true)
      : [];

    const preferredKeys = new Set(
      preferredFacetEntries.map((entry) => this.getEntryKey(entry))
    );

    const requiredFacetEntries = facetEntries.filter((entry) => {
      return !preferredKeys.has(this.getEntryKey(entry));
    });

    return {
      allIntentEntries,
      facetEntries,
      requiredFacetEntries,
      preferredFacetEntries,
      productQualifierEntries
    };
  }

  analyzeQuery(searchTerm) {
    const tokens = this.tokenizeQuery(searchTerm);
    const composableEntries = this.getComposableIntentEntries();
    const facetEntries = [];

    const compoundIntentChanged = this.applyCompoundIntentSuffixes(
      tokens,
      composableEntries,
      facetEntries
    );

    const exactIntentChanged = this.consumeExactIntentTerms(
      tokens,
      composableEntries,
      facetEntries
    );

    const fuzzyIntentChanged = this.consumeFuzzyIntentTerms(
      tokens,
      composableEntries,
      facetEntries
    );

    /*
     * Fuldstændige typeudtryk skal altid vinde over en mulig delvis
     * fortolkning af deres sidste ord. Det forhindrer fx "æterisk olie"
     * i samtidig at blive læst som begyndelsen på "olie blanding".
     */
    const trailingIntentPrefixChanged =
      this.consumeTrailingProductIntentPrefixes(
        tokens,
        composableEntries,
        facetEntries
      );

    const productTermBeforeGeneric = this.getRemainingSearchTerm(tokens);

    const genericCleanupChanged = this.stripGenericProductDescriptors(
      tokens,
      facetEntries
    );

    const rewriteChanged = this.rewriteRemainingProductTerms(tokens);

    const olfactiveIntent = this.consumeOlfactiveIntentTerms(
      tokens,
      facetEntries,
      searchTerm
    );

    const remainingSearchTerm = this.getRemainingSearchTerm(tokens);
    const classification = this.classifyFacetEntries(
      facetEntries,
      remainingSearchTerm
    );

    const searchTermVariants = [
      remainingSearchTerm,
      productTermBeforeGeneric
    ]
      .map((value) => this.normalizeTerm(value))
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index);

    return {
      originalTerm: String(searchTerm || "").trim(),
      searchTerm: remainingSearchTerm,
      searchTermVariants,
      ...classification,
      hasComposableIntent: classification.allIntentEntries.length > 0,
      isIntentOnly:
        classification.allIntentEntries.length > 0 && !remainingSearchTerm,
      hasProductCleanup:
        genericCleanupChanged ||
        rewriteChanged ||
        classification.productQualifierEntries.length > 0,
      hasOlfactiveCombination: olfactiveIntent.entries.length >= 2,
      intentWasConsumed:
        compoundIntentChanged ||
        trailingIntentPrefixChanged ||
        exactIntentChanged ||
        fuzzyIntentChanged ||
        olfactiveIntent.changed
    };
  }

  getDamerauLevenshteinDistance(sourceValue, targetValue) {
    const source = Array.from(String(sourceValue || ""));
    const target = Array.from(String(targetValue || ""));

    if (!source.length) {
      return target.length;
    }

    if (!target.length) {
      return source.length;
    }

    const matrix = Array.from(
      { length: source.length + 1 },
      () => new Array(target.length + 1).fill(0)
    );

    for (let sourceIndex = 0; sourceIndex <= source.length; sourceIndex += 1) {
      matrix[sourceIndex][0] = sourceIndex;
    }

    for (let targetIndex = 0; targetIndex <= target.length; targetIndex += 1) {
      matrix[0][targetIndex] = targetIndex;
    }

    for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
      for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
        const substitutionCost =
          source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;

        matrix[sourceIndex][targetIndex] = Math.min(
          matrix[sourceIndex - 1][targetIndex] + 1,
          matrix[sourceIndex][targetIndex - 1] + 1,
          matrix[sourceIndex - 1][targetIndex - 1] + substitutionCost
        );

        const isAdjacentTransposition =
          sourceIndex > 1 &&
          targetIndex > 1 &&
          source[sourceIndex - 1] === target[targetIndex - 2] &&
          source[sourceIndex - 2] === target[targetIndex - 1];

        if (isAdjacentTransposition) {
          matrix[sourceIndex][targetIndex] = Math.min(
            matrix[sourceIndex][targetIndex],
            matrix[sourceIndex - 2][targetIndex - 2] + 1
          );
        }
      }
    }

    return matrix[source.length][target.length];
  }

  isFuzzyEntryMatch(entry, searchTerm) {
    if (!entry?.fuzzy_match) {
      return false;
    }

    const normalizedSearchTerm = this.normalizeFuzzyTerm(searchTerm);
    const minimumLength = Math.max(
      1,
      Number(entry.fuzzy_min_length || 5)
    );

    if (normalizedSearchTerm.length < minimumLength) {
      return false;
    }

    const maximumDistance = Math.max(
      0,
      Number(entry.max_edit_distance || 1)
    );

    const fuzzyTerms = Array.isArray(entry.fuzzy_terms)
      ? entry.fuzzy_terms
      : entry.terms;

    if (!Array.isArray(fuzzyTerms)) {
      return false;
    }

    const fuzzyExclusions = Array.isArray(entry.fuzzy_exclusions)
      ? entry.fuzzy_exclusions
          .map((term) => this.normalizeFuzzyTerm(term))
          .filter(Boolean)
      : [];

    if (fuzzyExclusions.includes(normalizedSearchTerm)) {
      return false;
    }

    return fuzzyTerms.some((term) => {
      const normalizedTerm = this.normalizeFuzzyTerm(term);

      if (!normalizedTerm) {
        return false;
      }

      if (normalizedTerm === normalizedSearchTerm) {
        return true;
      }

      /*
       * Et afkortet ord er et præfiksmatch, ikke en stavefejl.
       * Det er vigtigt for fx "organ", som ikke i sig selv er en
       * tydelig søgning efter økologiske produkter.
       */
      if (normalizedTerm.startsWith(normalizedSearchTerm)) {
        return false;
      }

      if (
        Math.abs(normalizedTerm.length - normalizedSearchTerm.length) >
        maximumDistance
      ) {
        return false;
      }

      return (
        this.getDamerauLevenshteinDistance(
          normalizedSearchTerm,
          normalizedTerm
        ) <= maximumDistance
      );
    });
  }

  getQuery() {
    return this.input?.value.trim() || "";
  }

  isQueryLongEnough(searchTerm) {
    return this.normalizeTerm(searchTerm).length >= this.minimumQueryLength;
  }

  async onSubmit(event) {
    const searchTerm = this.getQuery();

    event.preventDefault();

    if (!this.isQueryLongEnough(searchTerm)) {
      this.renderShortQueryMessage();
      return;
    }

    this.form.classList.add("loading");

    const destination =
      await this.resolveSearchDestinationForSubmit(searchTerm);

    window.location.assign(destination);
  }

  onInput() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    if (!this.isQueryLongEnough(searchTerm)) {
      this.renderShortQueryMessage();
      return;
    }

    this.form.classList.add("loading");
    this.getCombinedSearchResults(searchTerm);
  }

  renderShortQueryMessage() {
    this.abortController?.abort();
    this.abortController = null;
    ++this.requestId;

    this.form.classList.remove("loading");

    const message = this.getInterfaceText(
      "Skriv mindst 3 tegn for at søge.",
      "Enter at least 3 characters to search."
    );

    this.predictiveSearchResults.innerHTML = `
      <div
        id="predictive-search-results"
        data-predictive-search-results
      >
        <div
          class="pt-25 pb-25 border-top"
          data-esensera-short-query-message
          role="status"
          aria-live="polite"
        >
          <p class="m-0 heading-color">${this.escapeHtml(message)}</p>
        </div>
      </div>
    `;

    this.open();
  }

  startRequest() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    return {
      currentRequestId: ++this.requestId,
      signal: this.abortController.signal
    };
  }

  getGenericProductWords() {
    return new Set(
      (Array.isArray(
        this.getIntentLanguageConfig()?.generic_product_words
      )
        ? this.getIntentLanguageConfig().generic_product_words
        : []
      )
        .map((value) => this.normalizeFuzzyTerm(value))
        .filter(Boolean)
    );
  }

  isAllowedSearchMapPrefix(entry, normalizedSearchTerm, normalizedTerm) {
    if (
      !normalizedTerm.startsWith(normalizedSearchTerm) ||
      normalizedTerm.length <= normalizedSearchTerm.length
    ) {
      return false;
    }

    const configuredMinimum = Math.max(
      this.minimumQueryLength,
      Number(entry?.prefix_min_length || 0)
    );

    if (normalizedSearchTerm.length < configuredMinimum) {
      return false;
    }

    /*
     * Et generisk ord som "olie" eller "oil" må ikke alene aktivere
     * et længere typeudtryk som "olie blanding". Så snart kunden skriver
     * videre, fx "olie bl...", er præfikset igen entydigt nok.
     */
    if (
      this.getGenericProductWords().has(
        this.normalizeFuzzyTerm(normalizedSearchTerm)
      )
    ) {
      return false;
    }

    return true;
  }

  getSearchMapPrefixDetails(entry, searchTerm) {
    const normalizedSearchTerm = this.normalizeFuzzyTerm(searchTerm);

    if (!normalizedSearchTerm || !Array.isArray(entry?.terms)) {
      return null;
    }

    const prefixExclusions = Array.isArray(entry?.prefix_exclusions)
      ? entry.prefix_exclusions
          .map((term) => this.normalizeFuzzyTerm(term))
          .filter(Boolean)
      : [];

    if (prefixExclusions.includes(normalizedSearchTerm)) {
      return null;
    }

    const canonicalValues = [
      entry?.label,
      entry?.collection_label,
      entry?.filter_value,
      entry?.handle
    ]
      .map((value) => this.normalizeFuzzyTerm(value))
      .filter(Boolean);

    let bestMatch = null;

    entry.terms.forEach((term) => {
      const normalizedTerm = this.normalizeFuzzyTerm(term);

      if (
        !normalizedTerm ||
        !this.isAllowedSearchMapPrefix(
          entry,
          normalizedSearchTerm,
          normalizedTerm
        )
      ) {
        return;
      }

      const termWordCount = normalizedTerm.split(" ").filter(Boolean).length;
      const remainingLength = Math.max(
        0,
        normalizedTerm.length - normalizedSearchTerm.length
      );
      const canonicalPrefix = canonicalValues.some((value) => {
        return value.startsWith(normalizedSearchTerm);
      });

      /*
       * Et præfiks på den kanoniske værdi er stærkere end et præfiks,
       * som kun optræder inde i et længere synonym. Det gør fx "citr"
       * entydigt til Citrusy, selv om Zesty også har synonymet
       * "citrusskal". Kortere og et-ords synonymer vinder derefter.
       */
      const score =
        (canonicalPrefix ? 100000 : 0) +
        (termWordCount === 1 ? 10000 : 0) +
        Math.max(0, 1000 - remainingLength);

      if (!bestMatch || score > bestMatch.score) {
        bestMatch = {
          score,
          term: normalizedTerm
        };
      }
    });

    return bestMatch;
  }

  filterAmbiguousPredictivePrefixMatches(matchRecords) {
    const protectedKinds = new Set([
      "type",
      "scent_profile",
      "note",
      "aromatherapy",
      "organic",
      "olfactive_character"
    ]);

    const strictUniqueKinds = new Set([
      "note",
      "aromatherapy",
      "organic"
    ]);

    const exactKinds = new Set(
      matchRecords
        .filter((match) => match.matchType === "exact")
        .map((match) => match.entry?.kind)
        .filter(Boolean)
    );

    const prefixGroups = new Map();

    matchRecords.forEach((match) => {
      const kind = match.entry?.kind;

      if (
        match.matchType !== "prefix" ||
        !protectedKinds.has(kind) ||
        exactKinds.has(kind)
      ) {
        return;
      }

      if (!prefixGroups.has(kind)) {
        prefixGroups.set(kind, []);
      }

      prefixGroups.get(kind).push(match);
    });

    const allowedPrefixKeys = new Set();

    prefixGroups.forEach((matches, kind) => {
      /*
       * Note/Aromaterapi/Økologisk indeholder mange naturlige sprogtermer
       * med fælles begyndelser (fx "fremmer ..."). Her er intentionen kun
       * klar, når præfikset peger på præcis én entry. Vi må ikke vælge en
       * "vinder" alene fordi ét synonym tilfældigvis er et par tegn kortere.
       */
      if (strictUniqueKinds.has(kind) && matches.length !== 1) {
        return;
      }

      const sortedMatches = [...matches].sort((matchA, matchB) => {
        return (
          Number(matchB.prefixScore || 0) -
            Number(matchA.prefixScore || 0) ||
          Number(matchB.entry?.priority || 0) -
            Number(matchA.entry?.priority || 0)
        );
      });

      const bestMatch = sortedMatches[0];
      const secondMatch = sortedMatches[1];

      /*
       * Ved helt lige kandidater er begyndelsen reelt tvetydig, og ingen
       * af dem foreslås endnu. Ellers beholdes kun den tydeligste kandidat
       * inden for gruppen. Andre grupper kan stadig vises samtidigt, fx
       * Collection: Citrus og Olfactive character: Citrusy.
       */
      if (
        !bestMatch ||
        (secondMatch &&
          Number(secondMatch.prefixScore || 0) ===
            Number(bestMatch.prefixScore || 0))
      ) {
        return;
      }

      allowedPrefixKeys.add(this.getEntryKey(bestMatch.entry));
    });

    return matchRecords.filter((match) => {
      const kind = match.entry?.kind;

      if (
        match.matchType !== "prefix" ||
        !protectedKinds.has(kind)
      ) {
        return true;
      }

      if (exactKinds.has(kind)) {
        return false;
      }

      return allowedPrefixKeys.has(this.getEntryKey(match.entry));
    });
  }

  getStructuredPrefixCandidateEntries(searchTerm) {
    const normalizedSearchTerm = this.normalizeFuzzyTerm(searchTerm);

    if (
      normalizedSearchTerm.length < this.minimumQueryLength ||
      !Array.isArray(this.searchMap?.entries)
    ) {
      return [];
    }

    const structuredKinds = new Set([
      "type",
      "scent_profile",
      "note",
      "aromatherapy",
      "organic",
      "olfactive_character"
    ]);

    return this.searchMap.entries.filter((entry) => {
      if (
        !structuredKinds.has(entry?.kind) ||
        !Array.isArray(entry?.terms) ||
        entry.allow_prefix === false ||
        (entry.match_mode === "exact_only" &&
          entry.allow_predictive_prefix !== true)
      ) {
        return false;
      }

      const normalizedTerms = entry.terms
        .map((term) => this.normalizeFuzzyTerm(term))
        .filter(Boolean);

      if (
        normalizedTerms.includes(normalizedSearchTerm) ||
        !normalizedTerms.some((term) =>
          term.startsWith(normalizedSearchTerm)
        )
      ) {
        return false;
      }

      /*
       * Eksplicit udelukkede præfikser er stadig vigtige her: de fortæller
       * os netop, at kunden er midt i et struktureret ord, men endnu ikke
       * har skrevet nok til, at intentionen må aktiveres. I den fase må
       * produkt-synonymer og title-prefix ikke få fragmentet til at ligne
       * et ægte produktmatch (fx "ånd"/"ånde" -> "åndedræt").
       */
      const prefixExclusions = Array.isArray(entry?.prefix_exclusions)
        ? entry.prefix_exclusions
            .map((term) => this.normalizeFuzzyTerm(term))
            .filter(Boolean)
        : [];

      if (prefixExclusions.includes(normalizedSearchTerm)) {
        return true;
      }

      return Boolean(this.getSearchMapPrefixDetails(entry, searchTerm));
    });
  }

  shouldAllowProductPrefixMatches(searchTerm) {
    const candidates = this.getStructuredPrefixCandidateEntries(searchTerm);

    if (!candidates.length) {
      return true;
    }

    const candidateKeys = new Set(
      candidates.map((entry) => this.getEntryKey(entry))
    );

    const resolvedPrefixEntries = this.getSearchMapMatches(searchTerm, {
      allowPrefix: true
    }).filter((entry) => candidateKeys.has(this.getEntryKey(entry)));

    /*
     * Et produkt-prefix er kun sikkert, når search map samtidig kan pege
     * på mindst én entydig struktureret kandidat. Hele ord i titel og
     * beskrivelse må stadig matche, selv når et fragment er uafklaret.
     */
    return resolvedPrefixEntries.length > 0;
  }

  getSearchMapMatches(searchTerm, { allowPrefix = false } = {}) {
    if (!Array.isArray(this.searchMap?.entries)) {
      return [];
    }

    const normalizedSearchTerm = this.normalizeTerm(searchTerm);

    if (!normalizedSearchTerm) {
      return [];
    }

    const matchRecords = this.searchMap.entries
      .map((entry) => {
        if (
          !["exact", "exact_only", "predictive_intent"].includes(
            entry.match_mode
          ) ||
          !Array.isArray(entry.terms)
        ) {
          return null;
        }

        const normalizedTerms = entry.terms
          .map((term) => this.normalizeTerm(term))
          .filter(Boolean);

        const exactMatch = normalizedTerms.some(
          (term) => term === normalizedSearchTerm
        );

        const mayUsePredictivePrefix =
          allowPrefix &&
          entry.allow_prefix !== false &&
          (entry.match_mode !== "exact_only" ||
            entry.allow_predictive_prefix === true);

        const prefixDetails =
          !exactMatch && mayUsePredictivePrefix
            ? this.getSearchMapPrefixDetails(entry, searchTerm)
            : null;

        const prefixMatch = Boolean(prefixDetails);

        const fuzzyMatch =
          !exactMatch &&
          !prefixMatch &&
          this.isFuzzyEntryMatch(entry, searchTerm);

        if (!exactMatch && !prefixMatch && !fuzzyMatch) {
          return null;
        }

        return {
          entry,
          matchRank: exactMatch ? 3 : prefixMatch ? 2 : 1,
          matchType: exactMatch
            ? "exact"
            : prefixMatch
              ? "prefix"
              : "fuzzy",
          prefixScore: prefixDetails?.score || 0
        };
      })
      .filter(Boolean);

    return this.filterAmbiguousPredictivePrefixMatches(matchRecords)
      .sort((matchA, matchB) => {
        const rankDifference = matchB.matchRank - matchA.matchRank;

        if (rankDifference !== 0) {
          return rankDifference;
        }

        const prefixScoreDifference =
          Number(matchB.prefixScore || 0) -
          Number(matchA.prefixScore || 0);

        if (prefixScoreDifference !== 0) {
          return prefixScoreDifference;
        }

        return (
          Number(matchB.entry.priority || 0) -
          Number(matchA.entry.priority || 0)
        );
      })
      .map((match) => match.entry);
  }

  getExactQueryFacetEntry(searchTerm) {
    if (!Array.isArray(this.searchMap?.entries)) {
      return null;
    }

    return (
      this.searchMap.entries
        .filter((entry) => {
          return (
            !this.shouldPreserveProductQuery(entry, searchTerm) &&
            ESENSERA_AUTO_SELECT_FACET_KINDS.has(entry?.kind) &&
            Boolean(entry?.filter_param) &&
            String(entry?.filter_value || "").trim() !== "" &&
            this.isExactEntryTermMatch(entry, searchTerm)
          );
        })
        .sort((entryA, entryB) => {
          return (
            Number(entryB?.priority || 0) -
            Number(entryA?.priority || 0)
          );
        })[0] || null
    );
  }

  getPredictiveTypeFacetEntry(searchTerm) {
    const matches = this.getUniqueEntries(
      this.getSearchMapMatches(searchTerm, {
        allowPrefix: true
      }).filter((entry) => {
        return (
          entry?.kind === "type" &&
          Boolean(entry?.filter_param) &&
          String(entry?.filter_value || "").trim() !== ""
        );
      })
    );

    return matches.length === 1 ? matches[0] : null;
  }

  /*
   * Kun search-map entries med en eksplicit liste må forbruge en
   * ufærdig søgestreng som et struktureret filter. Dermed bliver
   * fx "ånde" entydigt, mens det bredere ord "luft" forbliver
   * fritekst og stadig kan matche produktbeskrivelser.
   */
  getConfiguredPredictiveFacetEntry(searchTerm) {
    const normalizedSearchTerm = this.normalizeTerm(searchTerm);

    if (!normalizedSearchTerm || !Array.isArray(this.searchMap?.entries)) {
      return null;
    }

    const matches = this.getUniqueEntries(
      this.searchMap.entries.filter((entry) => {
        if (
          !ESENSERA_AUTO_SELECT_FACET_KINDS.has(entry?.kind) ||
          !Boolean(entry?.filter_param) ||
          String(entry?.filter_value || "").trim() === "" ||
          !Array.isArray(entry?.auto_select_prefix_terms)
        ) {
          return false;
        }

        const minimumLength = Math.max(
          this.minimumQueryLength,
          Number(entry?.auto_select_prefix_min_length || 0)
        );

        if (normalizedSearchTerm.length < minimumLength) {
          return false;
        }

        return entry.auto_select_prefix_terms.some((term) => {
          return this.isAllowedSearchMapPrefix(
            entry,
            normalizedSearchTerm,
            this.normalizeTerm(term)
          );
        });
      })
    );

    return matches.length === 1 ? matches[0] : null;
  }

  getPredictiveFacetEntry(searchTerm) {
    const matches = this.getUniqueEntries([
      this.getPredictiveTypeFacetEntry(searchTerm),
      this.getConfiguredPredictiveFacetEntry(searchTerm)
    ].filter(Boolean));

    return matches.length === 1 ? matches[0] : null;
  }

  getDirectDestinationEntry(searchTerm) {
    return (
      this.getSearchMapMatches(searchTerm, {
        allowPrefix: false
      }).find((entry) => {
        return (
          entry?.direct_destination === true &&
          Boolean(entry?.url) &&
          this.isExactEntryTermMatch(entry, searchTerm)
        );
      }) || null
    );
  }

  isExactEntryTermMatch(entry, searchTerm) {
    const normalizedSearchTerm = this.normalizeTerm(searchTerm);
    const submitTerms = Array.isArray(entry?.submit_terms)
      ? entry.submit_terms
      : entry?.terms;

    if (!normalizedSearchTerm || !Array.isArray(submitTerms)) {
      return false;
    }

    return submitTerms.some((term) => {
      return this.normalizeTerm(term) === normalizedSearchTerm;
    });
  }

  isNavigationOnlyTerm(entry, searchTerm) {
    const normalizedSearchTerm = this.normalizeTerm(searchTerm);

    if (!normalizedSearchTerm || !entry) {
      return false;
    }

    if (
      entry.navigation_only === true &&
      this.isExactEntryTermMatch(entry, searchTerm)
    ) {
      return true;
    }

    const navigationTerms = Array.isArray(entry.navigation_only_terms)
      ? entry.navigation_only_terms
      : [];

    return navigationTerms.some((term) => {
      return this.normalizeTerm(term) === normalizedSearchTerm;
    });
  }

  getNavigationOnlyContext(searchTerm) {
    const exactMatches = this.getSearchMapMatches(searchTerm, {
      allowPrefix: false
    });

    const entry = exactMatches.find((candidate) => {
      return this.isNavigationOnlyTerm(candidate, searchTerm);
    });

    if (!entry) {
      return { active: false, entry: null, suggestionEntries: [] };
    }

    const expandKind = String(entry.expand_suggestions_kind || "").trim();
    const suggestionEntries = expandKind && Array.isArray(this.searchMap?.entries)
      ? this.searchMap.entries
          .filter((candidate) => {
            return candidate?.kind === expandKind && Boolean(candidate?.url);
          })
          .sort((entryA, entryB) => {
            return Number(entryB?.priority || 0) - Number(entryA?.priority || 0);
          })
      : [entry];

    return {
      active: true,
      entry,
      suggestionEntries: this.getUniqueEntries(suggestionEntries)
    };
  }

  shouldPreserveProductQuery(entry, searchTerm) {
    const normalizedSearchTerm = this.normalizeTerm(searchTerm);
    const priorityTerms = Array.isArray(
      entry?.product_title_priority_terms
    )
      ? entry.product_title_priority_terms
      : [];

    return priorityTerms.some((term) => {
      return this.normalizeTerm(term) === normalizedSearchTerm;
    });
  }

  getExactCollectionDestinationEntry(searchTerm) {
    if (!Array.isArray(this.searchMap?.entries)) {
      return null;
    }

    return (
      this.searchMap.entries
        .filter((entry) => {
          return (
            entry?.direct_collection_destination === true &&
            Boolean(entry?.url) &&
            !this.shouldPreserveProductQuery(entry, searchTerm) &&
            this.isExactEntryTermMatch(entry, searchTerm)
          );
        })
        .sort((entryA, entryB) => {
          return (
            Number(entryB?.priority || 0) -
            Number(entryA?.priority || 0)
          );
        })[0] || null
    );
  }

  getQueryFacetContext(searchTerm) {
    let analysis = this.analyzeQuery(searchTerm);
    let entries = [...analysis.facetEntries];

    /*
     * Et helt, eksakt search-map-term kan klassificeres som struktureret
     * katalogintention. Præfikser bruges kun til forslag/rangering og må
     * aldrig forbruge den frie søgestreng eller vælge et filter.
     */
    if (!entries.length) {
      const exactFacetEntry = this.getExactQueryFacetEntry(searchTerm);

      if (exactFacetEntry) {
        entries = [exactFacetEntry];

        const classification = this.classifyFacetEntries(entries, "");

        analysis = {
          ...analysis,
          searchTerm: "",
          searchTermVariants: [],
          ...classification,
          hasComposableIntent: true,
          isIntentOnly: true,
          intentWasConsumed: true
        };
      }
    }

    return {
      analysis,
      entries: this.getUniqueEntries(entries)
    };
  }
  applyFacetEntryToParams(params, entry) {
    const paramName = String(entry?.filter_param || "").trim();
    const filterValue = String(entry?.filter_value || "").trim();

    if (!paramName || !filterValue) {
      return false;
    }

    const currentValues = params.getAll(paramName);
    const hasMatchingValue = currentValues.some((value) => {
      return (
        this.normalizeTerm(value) === this.normalizeTerm(filterValue)
      );
    });

    const isSingleValueFacet =
      paramName === "filter.p.product_type" ||
      paramName === "filter.p.m.custom.scent_profile" ||
      paramName === "filter.p.m.custom.aromatherapy" ||
      paramName === "filter.p.m.custom.custom_product_badge";

    if (isSingleValueFacet) {
      if (currentValues.length === 1 && hasMatchingValue) {
        return false;
      }

      params.delete(paramName);
      params.set(paramName, filterValue);
      return true;
    }

    if (hasMatchingValue) {
      return false;
    }

    params.append(paramName, filterValue);
    return true;
  }

  applyExactQueryFacetDefault(params, searchTerm) {
    const entry = this.getExactQueryFacetEntry(searchTerm);

    if (!entry) {
      return false;
    }

    return this.applyFacetEntryToParams(params, entry);
  }

  applyQueryFacetDefaults(
    params,
    searchTerm,
    context = null,
    { includePreferred = true } = {}
  ) {
    const queryContext = context || this.getQueryFacetContext(searchTerm);
    const entries = includePreferred
      ? queryContext.entries
      : queryContext.analysis.requiredFacetEntries;

    let changed = false;

    entries.forEach((entry) => {
      changed = this.applyFacetEntryToParams(params, entry) || changed;
    });

    return {
      ...queryContext,
      appliedEntries: entries,
      changed
    };
  }

  getContentMatches(searchTerm) {
    if (!Array.isArray(this.contentIndex?.items)) {
      return {
        pages: [],
        articles: []
      };
    }

    const normalizedSearchTerm = this.normalizeTerm(searchTerm);

    if (normalizedSearchTerm.length < this.minimumQueryLength) {
      return {
        pages: [],
        articles: []
      };
    }

    const matchedItems = this.contentIndex.items
      .map((item) => {
        const matchScore = this.getContentMatchScore(
          item,
          normalizedSearchTerm
        );

        if (matchScore <= 0) {
          return null;
        }

        return {
          item,
          matchScore,
          priority: Number(item.priority || 0)
        };
      })
      .filter(Boolean)
      .sort((matchA, matchB) => {
        const scoreDifference = matchB.matchScore - matchA.matchScore;

        if (scoreDifference !== 0) {
          return scoreDifference;
        }

        const priorityDifference = matchB.priority - matchA.priority;

        if (priorityDifference !== 0) {
          return priorityDifference;
        }

        return String(matchA.item.title || "").localeCompare(
          String(matchB.item.title || ""),
          this.isDanish() ? "da" : "en"
        );
      });

    return {
      pages: matchedItems
        .filter(({ item }) => item.type === "page")
        .slice(0, this.maximumPredictivePages)
        .map(({ item }) => item),
      articles: matchedItems
        .filter(({ item }) => item.type === "article")
        .slice(0, this.maximumPredictiveArticles)
        .map(({ item }) => item)
    };
  }

  getContentMatchScore(item, normalizedSearchTerm) {
    const normalizedTitle = this.normalizeTerm(item?.title);
    const normalizedTerms = Array.isArray(item?.terms)
      ? item.terms.map((term) => this.normalizeTerm(term)).filter(Boolean)
      : [];

    if (!normalizedTitle && !normalizedTerms.length) {
      return 0;
    }

    if (normalizedTitle === normalizedSearchTerm) {
      return 100;
    }

    if (normalizedTerms.some((term) => term === normalizedSearchTerm)) {
      return 95;
    }

    if (normalizedTitle.startsWith(normalizedSearchTerm)) {
      return 90;
    }

    if (this.hasWordPrefix(normalizedTitle, normalizedSearchTerm)) {
      return 85;
    }

    if (
      normalizedTerms.some((term) => term.startsWith(normalizedSearchTerm))
    ) {
      return 80;
    }

    if (normalizedTitle.includes(normalizedSearchTerm)) {
      return 70;
    }

    if (
      normalizedTerms.some((term) => term.includes(normalizedSearchTerm))
    ) {
      return 60;
    }

    return 0;
  }

  hasWordPrefix(value, normalizedSearchTerm) {
    if (!value || !normalizedSearchTerm) {
      return false;
    }

    return value
      .split(/[^\p{L}\p{N}]+/u)
      .filter(Boolean)
      .some((word) => word.startsWith(normalizedSearchTerm));
  }

  getPredictiveSearchUrl(searchTerm) {
    const searchParams = new URLSearchParams();

    searchParams.set("q", searchTerm);
    searchParams.set("section_id", "predictive-search");
    searchParams.set("resources[type]", "product,collection,query");
    searchParams.set("resources[limit]", String(this.maximumPredictiveProducts));
    searchParams.set(
      "resources[options][unavailable_products]",
      "show"
    );
    searchParams.set("resources[options][prefix]", "last");
    searchParams.set(
      "resources[options][fields]",
      "title,variants.title,variants.sku"
    );

    const predictiveRoute =
      typeof routes !== "undefined" && routes.predictive_search_url
        ? routes.predictive_search_url
        : "/search/suggest";

    return `${predictiveRoute}?${searchParams.toString()}`;
  }

  buildTitleFieldQuery(searchTerm, { prefix = true } = {}) {
    const words = this.normalizeTerm(searchTerm).match(
      /[\p{L}\p{N}][\p{L}\p{N}'’\-]*/gu
    );

    if (!words?.length) {
      return "";
    }

    return words
      .map((word) => {
        const suffix = prefix ? "*" : "";
        return `title:${this.escapeSearchToken(word)}${suffix}`;
      })
      .join(" ");
  }

  escapeSearchToken(value) {
    return String(value || "")
      .replace(/\\/g, "\\\\")
      .replace(/([:()])/g, "\\$1")
      .replace(/\*/g, "\\*");
  }

  getSearchRoute() {
    return (
      (typeof routes !== "undefined" && routes.search_url) ||
      this.form?.getAttribute("action") ||
      "/search"
    );
  }

  buildProductSearchUrl(searchTerm, mode = "broad") {
    const normalizedTerm = this.normalizeTerm(searchTerm);

    if (!normalizedTerm) {
      return null;
    }

    const requestUrl = new URL(
      this.getSearchRoute(),
      window.location.origin
    );

    let query = normalizedTerm;
    let prefixOption = "last";

    if (mode === "title-exact") {
      query = this.buildTitleFieldQuery(normalizedTerm, {
        prefix: false
      });
      prefixOption = "none";
    } else if (mode === "title-prefix") {
      query = this.buildTitleFieldQuery(normalizedTerm, {
        prefix: true
      });
      prefixOption = "none";
    }

    if (!query) {
      return null;
    }

    requestUrl.searchParams.set("q", query);
    requestUrl.searchParams.set("type", "product");
    requestUrl.searchParams.set("options[prefix]", prefixOption);
    requestUrl.searchParams.set(
      "options[unavailable_products]",
      "show"
    );
    requestUrl.searchParams.set(
      "options[fields]",
      "title,product_type,tag,variants.title,variants.sku"
    );
    requestUrl.searchParams.set(
      "section_id",
      "esensera-predictive-products"
    );

    return requestUrl.toString();
  }

  getProductSearchSources(analysis) {
    const searchTerm = this.normalizeTerm(analysis?.searchTerm);

    if (!searchTerm) {
      return [];
    }

    const allowPrefixMatches =
      this.shouldAllowProductPrefixMatches(
        analysis?.originalTerm || searchTerm
      );

    const getLocalTitleRank = (product) => {
      return this.getCatalogTitleMatchRank(product?.title, searchTerm);
    };

    const candidates = [
      {
        url: this.buildProductSearchUrl(searchTerm, "title-exact"),
        searchTier: "title-exact",
        /*
         * Shopify kan på enkelte Unicode-/prefix-forespørgsler returnere
         * en langt bredere section end selve title-queryen. Verificér altid
         * titlen lokalt, så et edge-case aldrig kan blive til hele kataloget.
         */
        productPredicate: (product) => {
          const rank = getLocalTitleRank(product);
          return rank !== null && rank <= 1;
        }
      },
      {
        url: this.buildProductSearchUrl(searchTerm, "title-prefix"),
        searchTier: "title-prefix",
        productPredicate: (product) => {
          const rank = getLocalTitleRank(product);

          if (rank === null) {
            return false;
          }

          return rank <= (allowPrefixMatches ? 2 : 1);
        }
      }
    ].filter((source) => Boolean(source.url));

    const seen = new Set();

    return candidates.filter((source) => {
      const comparableUrl = this.normalizeComparableUrl(source.url);

      if (!comparableUrl || seen.has(comparableUrl)) {
        return false;
      }

      seen.add(comparableUrl);
      return true;
    });
  }

  getCatalogProductsUrl() {
    const allProductsUrl =
      this.searchMap?.all_products_url ||
      `${window.Shopify?.routes?.root || "/"}collections/all`;

    const requestUrl = new URL(
      allProductsUrl,
      window.location.origin
    );

    requestUrl.searchParams.set("sort_by", "title-ascending");
    requestUrl.searchParams.set(
      "section_id",
      "esensera-predictive-catalog-products"
    );

    return requestUrl.toString();
  }

  getCatalogProductResponse() {
    if (this.catalogProductResponsePromise) {
      return this.catalogProductResponsePromise;
    }

    this.catalogProductResponsePromise = fetch(
      this.getCatalogProductsUrl(),
      {
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Produktkatalog: HTTP ${response.status}`
          );
        }

        return response.text();
      })
      .catch((error) => {
        this.catalogProductResponsePromise = null;
        throw error;
      });

    return this.catalogProductResponsePromise;
  }

  getCatalogQueryVariants(analysis) {
    const sourceValues =
      typeof analysis === "string"
        ? [analysis]
        : [
            analysis?.searchTerm,
            ...(Array.isArray(analysis?.searchTermVariants)
              ? analysis.searchTermVariants
              : [])
          ];

    return sourceValues
      .map((value) => this.normalizeFuzzyTerm(value))
      .filter(Boolean)
      .filter((value, index, values) => values.indexOf(value) === index);
  }

  toSearchSynonymArray(value) {
    if (Array.isArray(value)) {
      return value.flatMap((item) => this.toSearchSynonymArray(item));
    }

    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      return [];
    }

    if (typeof value === "object" && "value" in value) {
      return this.toSearchSynonymArray(value.value);
    }

    return String(value)
      .split(/\r?\n|;/u)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  getCatalogTitleMatchRank(title, normalizedQuery) {
    const normalizedTitle = this.normalizeFuzzyTerm(title);
    const query = this.normalizeFuzzyTerm(normalizedQuery);

    if (!normalizedTitle || !query) {
      return null;
    }

    if (normalizedTitle === query) {
      return 0;
    }

    if (` ${normalizedTitle} `.includes(` ${query} `)) {
      return 1;
    }

    const titleTokens = normalizedTitle.split(" ").filter(Boolean);
    const queryTokens = query.split(" ").filter(Boolean);

    if (
      queryTokens.length &&
      queryTokens.every((queryToken) => {
        return titleTokens.some((titleToken) => {
          return (
            titleToken === queryToken ||
            titleToken.startsWith(queryToken)
          );
        });
      })
    ) {
      return 2;
    }

    return null;
  }

  findTokenSequence(sourceTokens, targetTokens) {
    if (!sourceTokens.length || !targetTokens.length) {
      return -1;
    }

    for (
      let startIndex = 0;
      startIndex <= sourceTokens.length - targetTokens.length;
      startIndex += 1
    ) {
      const matches = targetTokens.every((targetToken, offset) => {
        return sourceTokens[startIndex + offset] === targetToken;
      });

      if (matches) {
        return startIndex;
      }
    }

    return -1;
  }

  getCatalogSynonymMatchRank(
    product,
    normalizedQuery,
    { allowPrefix = true } = {}
  ) {
    const query = this.normalizeFuzzyTerm(normalizedQuery);

    if (!query) {
      return null;
    }

    const queryTokens = query.split(" ").filter(Boolean);
    let bestRank = null;

    this.toSearchSynonymArray(product?.searchSynonyms).forEach((synonym) => {
      const normalizedSynonym = this.normalizeFuzzyTerm(synonym);

      if (!normalizedSynonym) {
        return;
      }

      if (normalizedSynonym === query) {
        bestRank = bestRank === null ? 0 : Math.min(bestRank, 0);
        return;
      }

      if (
        allowPrefix &&
        query.length >= this.minimumQueryLength &&
        normalizedSynonym.startsWith(query)
      ) {
        bestRank = bestRank === null ? 1 : Math.min(bestRank, 1);
        return;
      }

      const synonymTokens = normalizedSynonym.split(" ").filter(Boolean);
      const sequenceIndex = this.findTokenSequence(
        queryTokens,
        synonymTokens
      );

      if (sequenceIndex < 0) {
        return;
      }

      const remainingTokens = [
        ...queryTokens.slice(0, sequenceIndex),
        ...queryTokens.slice(sequenceIndex + synonymTokens.length)
      ];

      if (
        remainingTokens.length &&
        this.getCatalogTitleMatchRank(
          product?.title,
          remainingTokens.join(" ")
        ) !== null
      ) {
        bestRank = bestRank === null ? 2 : Math.min(bestRank, 2);
      }
    });

    return bestRank;
  }

  getComparableWordTokens(value) {
    return this.normalizeFuzzyTerm(value)
      .split(" ")
      .filter(Boolean);
  }

  hasExactWordSequence(value, query) {
    const sourceTokens = this.getComparableWordTokens(value);
    const queryTokens = this.getComparableWordTokens(query);

    if (!sourceTokens.length || !queryTokens.length) {
      return false;
    }

    return this.findTokenSequence(sourceTokens, queryTokens) >= 0;
  }

  getCatalogBodyMatchRank(product, normalizedQuery) {
    const query = this.normalizeFuzzyTerm(normalizedQuery);

    if (!query || !String(product?.searchBody || "").trim()) {
      return null;
    }

    /*
     * Beskrivelser er et sekundært discovery-lag. Her accepteres kun hele
     * ord/ordsekvenser — "ånde" må fx aldrig matche "åndedræt".
     */
    return this.hasExactWordSequence(product.searchBody, query) ? 0 : null;
  }

  getCatalogProductMatch(product, analysis) {
    const variants = this.getCatalogQueryVariants(analysis);
    const allowPrefixMatches =
      this.shouldAllowProductPrefixMatches(
        analysis?.originalTerm || analysis?.searchTerm || variants[0] || ""
      );

    let bestTitleRank = null;

    variants.forEach((variant) => {
      const rank = this.getCatalogTitleMatchRank(product?.title, variant);

      if (
        rank !== null &&
        (rank <= 1 || allowPrefixMatches)
      ) {
        bestTitleRank =
          bestTitleRank === null ? rank : Math.min(bestTitleRank, rank);
      }
    });

    if (bestTitleRank !== null) {
      return {
        kind: "title",
        rank: bestTitleRank
      };
    }

    let bestSynonymRank = null;

    variants.forEach((variant) => {
      const rank = this.getCatalogSynonymMatchRank(product, variant, {
        allowPrefix: allowPrefixMatches
      });

      if (rank !== null) {
        bestSynonymRank =
          bestSynonymRank === null
            ? rank
            : Math.min(bestSynonymRank, rank);
      }
    });

    if (bestSynonymRank !== null) {
      return {
        kind: "synonym",
        rank: bestSynonymRank
      };
    }

    let bestBodyRank = null;

    variants.forEach((variant) => {
      const rank = this.getCatalogBodyMatchRank(product, variant);

      if (rank !== null) {
        bestBodyRank =
          bestBodyRank === null ? rank : Math.min(bestBodyRank, rank);
      }
    });

    if (bestBodyRank !== null) {
      return {
        kind: "body",
        rank: bestBodyRank
      };
    }

    return null;
  }

  createCatalogProductResponses(text, analysis) {
    if (!text || !this.getCatalogQueryVariants(analysis).length) {
      return [];
    }

    const matchCache = new Map();
    const getMatch = (product) => {
      const key = String(product?.id || product?.handle || product?.url || "");

      if (!matchCache.has(key)) {
        matchCache.set(key, this.getCatalogProductMatch(product, analysis));
      }

      return matchCache.get(key);
    };

    return [
      {
        text,
        searchTier: "catalog-title",
        productRank: 15,
        sortAlphabetically: true,
        productPredicate: (product) => getMatch(product)?.kind === "title"
      },
      {
        text,
        searchTier: "catalog-synonym",
        productRank: 30,
        sortAlphabetically: true,
        productPredicate: (product) => getMatch(product)?.kind === "synonym"
      },
      {
        text,
        searchTier: "catalog-body",
        productRank: 60,
        sortAlphabetically: true,
        productPredicate: (product) => getMatch(product)?.kind === "body"
      }
    ];
  }

  getProductResponseRank(response) {
    if (Number.isFinite(Number(response?.productRank))) {
      return Number(response.productRank);
    }

    const ranks = {
      "title-exact": 10,
      "catalog-title": 15,
      "title-prefix": 20,
      "catalog-synonym": 30,
      mapped: 40,
      "catalog-body": 60,
      broad: 70,
      native: 90
    };

    return ranks[response?.searchTier] ?? 80;
  }

  getProductConstraintEntries(
    analysis,
    { includePreferred = true } = {}
  ) {
    const facetEntries = includePreferred
      ? analysis?.facetEntries
      : analysis?.requiredFacetEntries;

    return this.getUniqueEntries([
      ...(Array.isArray(analysis?.productQualifierEntries)
        ? analysis.productQualifierEntries
        : []),
      ...(Array.isArray(facetEntries) ? facetEntries : [])
    ]);
  }

  handleizePathTag(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/æ/g, "ae")
      .replace(/ø/g, "o")
      .replace(/å/g, "a")
      .replace(/Æ/g, "ae")
      .replace(/Ø/g, "o")
      .replace(/Å/g, "a")
      .toLocaleLowerCase("en-US")
      .trim()
      .replace(/&/g, "-and-")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  buildFacetCollectionUrl(
    entries,
    { sectionId = null, sortBy = null } = {}
  ) {
    const allProductsUrl =
      this.searchMap?.all_products_url ||
      `${window.Shopify?.routes?.root || "/"}collections/all`;

    const requestUrl = new URL(
      allProductsUrl,
      window.location.origin
    );

    const uniqueEntries = this.getUniqueEntries(entries);
    const tagHandles = [
      ...new Set(
        uniqueEntries
          .filter((entry) => entry?.filter_param === "filter.p.tag")
          .map((entry) => this.handleizePathTag(entry?.filter_value))
          .filter(Boolean)
      )
    ];

    requestUrl.searchParams.delete("filter.p.tag");

    uniqueEntries
      .filter((entry) => entry?.filter_param !== "filter.p.tag")
      .forEach((entry) => {
        this.applyFacetEntryToParams(requestUrl.searchParams, entry);
      });

    if (tagHandles.length) {
      requestUrl.pathname =
        `${requestUrl.pathname.replace(/\/$/, "")}/` +
        tagHandles.join("+");
    }

    if (sortBy) {
      requestUrl.searchParams.set("sort_by", sortBy);
    }

    if (sectionId) {
      requestUrl.searchParams.set("section_id", sectionId);
    }

    return requestUrl.toString();
  }

  getIntentProductSources(queryContext) {
    const analysis = queryContext?.analysis;
    const facetEntries = Array.isArray(analysis?.facetEntries)
      ? analysis.facetEntries
      : [];

    if (analysis?.searchTerm) {
      const productConstraintEntries =
        this.getProductConstraintEntries(analysis, {
          includePreferred: true
        });

      return this.getProductSearchSources(analysis).map((source) => ({
        ...source,
        facetEntries: productConstraintEntries,
        isIntentSource: true
      }));
    }

    if (!facetEntries.length) {
      return [];
    }

    const hasOrganicIntent = facetEntries.some(
      (entry) => entry?.kind === "organic"
    );

    const isPureTypeIntent =
      facetEntries.length === 1 && facetEntries[0]?.kind === "type";

    return [
      {
        url: this.buildFacetCollectionUrl(facetEntries, {
          sectionId: "esensera-predictive-products",
          sortBy: hasOrganicIntent
            ? "title-ascending"
            : isPureTypeIntent
              ? "best-selling"
              : null
        }),
        facetEntries,
        isIntentSource: true
      }
    ];
  }

  getMappedProductsUrl(entry) {
    if (!entry?.url) {
      return null;
    }

    try {
      const requestUrl = new URL(entry.url, window.location.origin);

      requestUrl.searchParams.set(
        "sort_by",
        entry.source_sort_by || "best-selling"
      );
      requestUrl.searchParams.set(
        "section_id",
        "esensera-predictive-products"
      );

      return requestUrl.toString();
    } catch (error) {
      console.error(
        "Ugyldig URL i ESENSERA search map:",
        entry.url,
        error
      );

      return null;
    }
  }

  getUniqueMappedEntries(matches) {
    const seenUrls = new Set();

    return matches.filter((entry) => {
      const comparableUrl = this.normalizeComparableUrl(entry?.url);

      if (!comparableUrl || seenUrls.has(comparableUrl)) {
        return false;
      }

      seenUrls.add(comparableUrl);
      return true;
    });
  }

  readProductIndexFromResponse(text) {
    if (!text) {
      return [];
    }

    const parsedHTML = new DOMParser().parseFromString(text, "text/html");
    const indexElement = parsedHTML.querySelector(
      "[data-esensera-predictive-product-index]"
    );

    if (!indexElement) {
      return [];
    }

    try {
      const products = JSON.parse(indexElement.textContent.trim() || "[]");
      return Array.isArray(products) ? products : [];
    } catch (error) {
      console.warn("Predictive produktindeks kunne ikke læses:", error);
      return [];
    }
  }

  countUniqueProductsInResponses(responses, facetEntries = null) {
    const seen = new Set();

    (Array.isArray(responses) ? responses : []).forEach((response) => {
      const { text } = response || {};
      const effectiveFacetEntries = Array.isArray(facetEntries)
        ? facetEntries
        : Array.isArray(response?.facetEntries)
          ? response.facetEntries
          : [];

      this.readProductIndexFromResponse(text).forEach((product) => {
        if (
          !this.productMatchesFacetEntries(product, effectiveFacetEntries) ||
          (typeof response?.productPredicate === "function" &&
            !response.productPredicate(product))
        ) {
          return;
        }

        const key =
          String(product?.id || "") ||
          String(product?.handle || "") ||
          this.normalizeComparableUrl(product?.url);

        if (key) {
          seen.add(key);
        }
      });
    });

    return seen.size;
  }

  filterUnavailableOlfactiveMatches(entries, catalogText) {
    const sourceEntries = Array.isArray(entries) ? entries : [];

    if (!catalogText) {
      /*
       * Olfactive Character-forslag kræver positiv bekræftelse fra det
       * aktuelle katalog. Hvis availability-indekset ikke kan læses,
       * skjules kun disse forslag frem for at vise potentielt tomme tags.
       */
      return sourceEntries.filter(
        (entry) => entry?.kind !== "olfactive_character"
      );
    }

    const products = this.readProductIndexFromResponse(catalogText);

    return sourceEntries.filter((entry) => {
      if (entry?.kind !== "olfactive_character") {
        return true;
      }

      return products.some((product) => {
        return this.productMatchesFacetEntry(product, entry);
      });
    });
  }

  selectBestProductSearchResponses(responses) {
    const sourceResponses = Array.isArray(responses) ? responses : [];
    const tierOrder = ["title-exact", "title-prefix", "broad"];
    const hasTieredSources = sourceResponses.some((response) =>
      tierOrder.includes(response?.searchTier)
    );

    if (!hasTieredSources) {
      return sourceResponses;
    }

    for (const tier of tierOrder) {
      const tierResponses = sourceResponses.filter(
        (response) => response?.searchTier === tier
      );

      if (
        tierResponses.length &&
        this.countUniqueProductsInResponses(tierResponses, []) > 0
      ) {
        return tierResponses;
      }
    }

    return sourceResponses;
  }

  async getCombinedSearchResults(searchTerm) {
    const queryContext = this.getQueryFacetContext(searchTerm);
    const { analysis } = queryContext;
    const navigationContext = this.getNavigationOnlyContext(searchTerm);

    const mapMatches = this.getSearchMapMatches(searchTerm, {
      allowPrefix: true
    });

    const matches = this.getUniqueEntries([
      ...analysis.facetEntries,
      ...mapMatches
    ]);

    const hasEnhancedProductIntent =
      analysis.facetEntries.length > 0 ||
      analysis.productQualifierEntries.length > 0 ||
      analysis.hasProductCleanup;

    const activeEntryKeys = new Set(
      queryContext.entries.map((entry) => this.getEntryKey(entry))
    );

    let visibleMatches = navigationContext.active
      ? navigationContext.suggestionEntries
      : matches.filter((entry) => {
          return (
            entry.suggestion_visible !== false &&
            ([
              "type",
              "scent_profile",
              "aromatherapy",
              "olfactive_character"
            ].includes(entry?.kind) ||
              !activeEntryKeys.has(this.getEntryKey(entry)) ||
              analysis.isPredictiveFacetPrefix === true)
          );
        });

    const mappedEntriesToFetch =
      navigationContext.active || hasEnhancedProductIntent
        ? []
        : this.getUniqueMappedEntries(matches);

    /*
     * Hent kun kontrollerede titelkilder. Produktbeskrivelser matches
     * separat og lokalt som hele ord via katalogindekset.
     */
    const intentProductSources = navigationContext.active
      ? []
      : this.getIntentProductSources(queryContext);

    const effectiveSearchTerm = analysis.searchTerm || searchTerm;
    const contentMatches = navigationContext.active
      ? { pages: [], articles: [] }
      : this.getContentMatches(effectiveSearchTerm);

    const { currentRequestId, signal } = this.startRequest();

    try {
      const predictiveRequest = fetch(
        this.getPredictiveSearchUrl(effectiveSearchTerm),
        { signal }
      ).then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search: HTTP ${response.status}`);
        }

        return response.text();
      });

      const mappedProductRequests = mappedEntriesToFetch.map((entry) => {
        const mappedProductsUrl = this.getMappedProductsUrl(entry);

        if (!mappedProductsUrl) {
          return Promise.resolve({
            entry,
            text: null,
            facetEntries: entry ? [entry] : [],
            searchTier: "mapped",
            productRank: 40,
            sortAlphabetically: true
          });
        }

        return fetch(mappedProductsUrl, { signal })
          .then((response) => {
            if (!response.ok) {
              return {
                entry,
                text: null,
                facetEntries: entry ? [entry] : [],
                searchTier: "mapped",
                productRank: 40,
                sortAlphabetically: true
              };
            }

            return response.text().then((text) => ({
              entry,
              text,
              facetEntries: entry ? [entry] : [],
              searchTier: "mapped",
              productRank: 40,
              sortAlphabetically: true
            }));
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              throw error;
            }

            console.warn("Mapped products kunne ikke hentes:", error);
            return {
              entry,
              text: null,
              facetEntries: entry ? [entry] : [],
              searchTier: "mapped",
              productRank: 40,
              sortAlphabetically: true
            };
          });
      });

      const intentProductRequests = intentProductSources.map((source) => {
        if (!source?.url) {
          return Promise.resolve({ ...source, text: null });
        }

        return fetch(source.url, { signal })
          .then((response) => {
            if (!response.ok) {
              return { ...source, text: null };
            }

            return response.text().then((text) => ({
              ...source,
              text
            }));
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              throw error;
            }

            console.warn("Intent-produkter kunne ikke hentes:", error);
            return { ...source, text: null };
          });
      });

      const [
        predictiveText,
        mappedProductResponses,
        intentProductResponses
      ] = await Promise.all([
        predictiveRequest,
        Promise.all(mappedProductRequests),
        Promise.all(intentProductRequests)
      ]);

      if (currentRequestId !== this.requestId) {
        return;
      }

      const directTitleResponses = intentProductResponses.filter(
        (response) =>
          ["title-exact", "title-prefix"].includes(response?.searchTier)
      );

      let directTitleProductCount =
        this.countUniqueProductsInResponses(directTitleResponses, []);

      const hasNativeTitleProducts = directTitleProductCount > 0;
      let catalogProductResponses = [];
      let catalogText = null;

      /*
       * Katalogindekset er den kontrollerede kilde til produktsynonymer,
       * eksakte ord i beskrivelser og availability-check af Olfactive
       * character. Det forhindrer, at en løs native søgning falder tilbage
       * til hele kataloget.
       */
      const needsCatalogIndex =
        !navigationContext.active &&
        (Boolean(analysis.searchTerm) ||
          visibleMatches.some(
            (entry) => entry?.kind === "olfactive_character"
          ));

      if (needsCatalogIndex) {
        try {
          catalogText = await this.getCatalogProductResponse();

          if (currentRequestId !== this.requestId) {
            return;
          }

          if (analysis.searchTerm) {
            catalogProductResponses =
              this.createCatalogProductResponses(catalogText, analysis);

            if (directTitleProductCount === 0) {
              directTitleProductCount =
                this.countUniqueProductsInResponses(
                  catalogProductResponses.filter(
                    (response) => response.searchTier === "catalog-title"
                  ),
                  []
                );
            }
          }

          visibleMatches = this.filterUnavailableOlfactiveMatches(
            visibleMatches,
            catalogText
          );
        } catch (error) {
          console.warn(
            "Produktkataloget til kontrolleret søgning kunne ikke hentes:",
            error
          );
        }
      }

      if (!navigationContext.active) {
        visibleMatches = visibleMatches.slice(
          0,
          this.maximumMappedSuggestions
        );
      }

      const parsedPredictiveHTML = new DOMParser().parseFromString(
        predictiveText,
        "text/html"
      );

      const returnedSection = parsedPredictiveHTML.querySelector(
        "#shopify-section-predictive-search"
      );

      if (!returnedSection) {
        throw new Error("Predictive search-sektionen manglede i svaret.");
      }

      this.predictiveSearchResults.innerHTML = returnedSection.innerHTML;

      const resultsRoot =
        this.predictiveSearchResults.querySelector(
          "[data-predictive-search-results]"
        ) ||
        this.predictiveSearchResults.querySelector(
          "#predictive-search-results"
        );

      if (!resultsRoot) {
        throw new Error("Resultatbeholderen manglede.");
      }

      this.removeNativeContentGroups(resultsRoot);

      if (navigationContext.active) {
        resultsRoot
          .querySelectorAll(
            '[data-predictive-group="queries"], [data-predictive-group="collections"]'
          )
          .forEach((group) => group.remove());
      }

      const mappedCollectionMatches = visibleMatches.filter((entry) => {
        return ["type", "scent_profile"].includes(entry?.kind);
      });

      const otherVisibleMatches = visibleMatches.filter((entry) => {
        return !["type", "scent_profile"].includes(entry?.kind);
      });

      this.ensureMappedCollectionSuggestions(
        resultsRoot,
        mappedCollectionMatches
      );

      let mappedEntriesGroup = null;

      if (otherVisibleMatches.length) {
        mappedEntriesGroup = this.createMappedEntriesGroup(
          otherVisibleMatches
        );
        resultsRoot.insertBefore(mappedEntriesGroup, resultsRoot.firstChild);
      }

      const prioritizedIntentResponses = (analysis.searchTerm
        ? this.selectBestProductSearchResponses(intentProductResponses)
        : intentProductResponses
      ).map((response) => ({
        ...response,
        productRank: this.getProductResponseRank(response),
        sortAlphabetically: [
          "title-exact",
          "title-prefix"
        ].includes(response?.searchTier)
      }));

      const candidateIntentResponses = [
        ...prioritizedIntentResponses,
        ...catalogProductResponses.filter((response) => {
          return !(
            hasNativeTitleProducts &&
            response?.searchTier === "catalog-title"
          );
        })
      ];

      let selectedConstraintEntries = this.getProductConstraintEntries(
        analysis,
        { includePreferred: true }
      );
      let usedSoftFallback = false;

      const strictProductCount = hasEnhancedProductIntent
        ? this.countUniqueProductsInResponses(
            candidateIntentResponses,
            selectedConstraintEntries
          )
        : null;

      if (
        hasEnhancedProductIntent &&
        analysis.preferredFacetEntries.length &&
        strictProductCount === 0
      ) {
        const fallbackConstraintEntries =
          this.getProductConstraintEntries(analysis, {
            includePreferred: false
          });

        const fallbackProductCount = this.countUniqueProductsInResponses(
          candidateIntentResponses,
          fallbackConstraintEntries
        );

        if (fallbackProductCount > 0) {
          selectedConstraintEntries = fallbackConstraintEntries;
          usedSoftFallback = true;
        }
      }

      const selectedIntentResponses = candidateIntentResponses.map(
        (response) => ({
          ...response,
          facetEntries: selectedConstraintEntries
        })
      );

      const productResponses = [
        ...selectedIntentResponses,
        ...mappedProductResponses
      ];

      const sortAlphabetically =
        analysis.isIntentOnly &&
        queryContext.entries.some((entry) => {
          return ["organic", "type"].includes(entry?.kind);
        });

      const isPureTypeIntent =
        analysis.isIntentOnly &&
        queryContext.entries.length === 1 &&
        queryContext.entries[0]?.kind === "type";

      const combinedProductsGroup = this.mergeProductResults({
        resultsRoot,
        mappedProductResponses: navigationContext.active
          ? []
          : productResponses,
        mappedEntriesGroup,
        /*
         * Native predictive produktkort bruges ikke som ukontrolleret
         * fallback. Alle viste produkter skal komme fra titel-, synonym-,
         * metadata- eller eksakt beskrivelsesmatch.
         */
        includeNativeProducts: false,
        sortAlphabetically,
        selectPopularBeforeAlphabeticalSort: isPureTypeIntent
      });

      const displayedProductCount = combinedProductsGroup
        ? combinedProductsGroup.querySelectorAll(
            ".grid-cols > *, [data-esensera-predictive-product-cards] > *"
          ).length
        : 0;

      this.latestSearchOutcome = {
        query: this.normalizeTerm(searchTerm),
        strictProductCount,
        directTitleProductCount,
        displayedProductCount,
        usedSoftFallback,
        isIntentOnly: analysis.isIntentOnly,
        entries: queryContext.entries
      };

      this.insertContentGroups({
        resultsRoot,
        contentMatches
      });

      if (hasEnhancedProductIntent) {
        this.updateIntentNoResultsMessage(
          resultsRoot,
          searchTerm,
          displayedProductCount === 0
        );
      } else if (
        visibleMatches.length ||
        displayedProductCount > 0 ||
        contentMatches.pages.length ||
        contentMatches.articles.length
      ) {
        this.removeNoResultsMessage(resultsRoot);
      }

      this.ensureMoreResultsFooter(resultsRoot, searchTerm);
      this.orderPredictiveGroups(resultsRoot);
      this.normalizePredictiveSeparators(resultsRoot);

      this.form.classList.remove("loading");
      this.open();
      this.lazyloadImage();
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }

      if (currentRequestId !== this.requestId) {
        return;
      }

      this.form.classList.remove("loading");
      this.close();

      console.error("Predictive search fejlede:", error);
    }
  }

  removeNativeContentGroups(resultsRoot) {
    resultsRoot
      .querySelectorAll(
        '[data-predictive-group="pages"], [data-predictive-group="articles"]'
      )
      .forEach((group) => group.remove());
  }

  removeNoResultsMessage(resultsRoot) {
    resultsRoot
      .querySelectorAll("[data-predictive-no-results]")
      .forEach((message) => message.remove());
  }

  updateIntentNoResultsMessage(
    resultsRoot,
    searchTerm,
    shouldShow
  ) {
    this.removeNoResultsMessage(resultsRoot);

    if (!shouldShow) {
      return;
    }

    const message = document.createElement("p");
    const label = this.getInterfaceText(
      `Ingen produkter fundet for “${searchTerm}”.`,
      `No products found for “${searchTerm}”.`
    );

    message.className =
      "m-0 fs-18 heading-color mb-20 word-break-all esensera-predictive-search-label";
    message.setAttribute("data-predictive-no-results", "");
    message.setAttribute("data-esensera-intent-no-results", "");
    message.setAttribute("role", "status");
    message.setAttribute("aria-live", "polite");
    message.textContent = label;

    resultsRoot.insertBefore(message, resultsRoot.firstChild);
  }

  createMappedEntriesGroup(matches) {
    const group = document.createElement("div");

    group.setAttribute("data-esensera-mapped-entries", "");
    group.setAttribute("data-predictive-group", "mapped-entries");

    matches.forEach((entry, index) => {
      const block = this.createMappedEntryBlock(entry);

      if (index > 0) {
        block.classList.add("bm-predictive-entry-block--separated");
      }

      group.appendChild(block);
    });

    return group;
  }

  createMappedEntryBlock(entry) {
    const block = document.createElement("div");
    block.className = "bm-predictive-entry-block";

    const heading = document.createElement("p");
    heading.className =
      "m-0 esensera-predictive-kicker word-break-all";
    heading.textContent = this.getGroupLabel(entry.kind);

    const link = document.createElement("a");
    link.className =
      "quick-url inline-block hover:underline mb-5 last-0 predictive-search__item";
    link.href = entry.url;
    link.setAttribute("aria-label", entry.label);
    link.setAttribute("aria-selected", "false");
    link.setAttribute("role", "option");

    link.appendChild(document.createTextNode(entry.label));

    block.appendChild(heading);
    block.appendChild(link);

    return block;
  }

  getCollectionSuggestionLabel(entry) {
    if (entry?.kind === "type") {
      return String(
        entry?.collection_label || entry?.label || entry?.filter_value || ""
      ).trim();
    }

    return String(
      entry?.collection_label || entry?.label || entry?.filter_value || ""
    ).trim();
  }

  appendScentProfileDot(link, entry) {
    /* Brana 2.0: predictive-search collection/facet suggestions are purely
       typographic. Colour dots belong to product metadata, not this list. */
    return;
  }

  createMappedCollectionSuggestionLink(entry) {
    const link = document.createElement("a");
    const targetUrl = entry?.url || this.buildFacetCollectionUrl([entry]);
    const label = this.getCollectionSuggestionLabel(entry);

    link.className =
      "quick-url inline-block hover:underline mb-5 last-0 predictive-search__item";
    link.href = targetUrl;
    link.setAttribute("aria-label", label);
    link.setAttribute("aria-selected", "false");
    link.setAttribute("role", "option");
    link.setAttribute("data-predictive-collection", "");
    link.setAttribute("data-esensera-mapped-collection", "");
    link.setAttribute("data-collection-handle", String(entry?.handle || ""));
    link.setAttribute("data-collection-title", label);
    link.setAttribute("data-collection-target-url", targetUrl);

    if (entry?.kind === "type") {
      link.setAttribute("data-esensera-mapped-type-collection", "");
    }

    if (entry?.kind === "scent_profile") {
      link.setAttribute("data-esensera-mapped-profile-collection", "");
      this.appendScentProfileDot(link, entry);
    }

    link.appendChild(document.createTextNode(label));

    return link;
  }

  ensureMappedCollectionSuggestions(resultsRoot, entries) {
    const seenUrls = new Set();
    const collectionEntries = this.getUniqueEntries(entries).filter((entry) => {
      if (
        !["type", "scent_profile"].includes(entry?.kind) ||
        !entry?.url
      ) {
        return false;
      }

      const comparableUrl = this.normalizeComparableUrl(entry.url);

      if (!comparableUrl || seenUrls.has(comparableUrl)) {
        return false;
      }

      seenUrls.add(comparableUrl);
      return true;
    });

    if (!collectionEntries.length) {
      return null;
    }

    let collectionGroup = Array.from(resultsRoot.children).find((child) => {
      return child.matches?.('[data-predictive-group="collections"]');
    });

    if (!collectionGroup) {
      collectionGroup = document.createElement("div");
      collectionGroup.className = "pt-25 pb-25 border-top";
      collectionGroup.setAttribute("data-predictive-group", "collections");
      collectionGroup.setAttribute(
        "data-esensera-generated-collection-group",
        ""
      );

      const heading = document.createElement("p");
      heading.className =
        "m-0 esensera-predictive-kicker word-break-all";
      heading.textContent = this.getInterfaceText(
        "Kollektioner",
        "Collections"
      );

      collectionGroup.appendChild(heading);
      resultsRoot.appendChild(collectionGroup);
    }

    const collectionHeading = collectionGroup.querySelector("p");

    if (collectionHeading) {
      collectionHeading.classList.add("esensera-predictive-kicker");
      collectionHeading.classList.remove(
        "fs-18",
        "heading-color",
        "btn-text-transform",
        "mb-15"
      );
    }

    const mappedHandles = new Set(
      collectionEntries
        .map((entry) => this.normalizeTerm(entry?.handle))
        .filter(Boolean)
    );

    const mappedUrls = new Set(
      collectionEntries
        .map((entry) => this.normalizeComparableUrl(entry?.url))
        .filter(Boolean)
    );

    /*
     * Shopify kan allerede have returneret den samme collection. Den
     * native variant fjernes og erstattes med den mapperede variant, så
     * label, destination og profilprik altid er ens.
     */
    collectionGroup
      .querySelectorAll("[data-predictive-collection]")
      .forEach((collectionLink) => {
        const handle = this.normalizeTerm(
          collectionLink.getAttribute("data-collection-handle")
        );
        const url = this.normalizeComparableUrl(
          collectionLink.getAttribute("data-collection-target-url") ||
            collectionLink.getAttribute("href")
        );

        if (mappedHandles.has(handle) || mappedUrls.has(url)) {
          collectionLink.remove();
        }
      });

    const fragment = document.createDocumentFragment();

    collectionEntries.forEach((entry) => {
      fragment.appendChild(this.createMappedCollectionSuggestionLink(entry));
    });

    const heading = collectionGroup.querySelector("p");
    const firstCollectionLink = collectionGroup.querySelector(
      "[data-predictive-collection]"
    );

    collectionGroup.insertBefore(
      fragment,
      firstCollectionLink || heading?.nextSibling || null
    );

    return collectionGroup;
  }

  orderPredictiveGroups(resultsRoot) {
    const groupPriority = new Map([
      ["queries", 10],
      ["collections", 20],
      ["mapped-entries", 30],
      ["products", 40],
      ["pages", 50],
      ["articles", 60]
    ]);

    const footer = resultsRoot.querySelector("[data-predictive-footer]");
    const orderedGroups = Array.from(resultsRoot.children)
      .map((group, originalIndex) => ({
        group,
        originalIndex,
        kind: group.getAttribute?.("data-predictive-group") || ""
      }))
      .filter(({ kind }) => groupPriority.has(kind))
      .sort((groupA, groupB) => {
        return (
          Number(groupPriority.get(groupA.kind)) -
            Number(groupPriority.get(groupB.kind)) ||
          groupA.originalIndex - groupB.originalIndex
        );
      });

    orderedGroups.forEach(({ group }) => {
      resultsRoot.insertBefore(group, footer || null);
    });
  }

  normalizePredictiveSeparators(resultsRoot) {
    const groups = Array.from(resultsRoot.children).filter((child) => {
      return (
        child.nodeType === Node.ELEMENT_NODE &&
        !child.matches?.("script, style, [data-predictive-footer]")
      );
    });

    groups.forEach((group, index) => {
      /* Brana 2.0: separators are class-driven so the same amount of air can
         be applied above and below every divider. */
      group.classList.remove(
        "border-top",
        "mt-25",
        "pt-40",
        "pb-15",
        "pt-25",
        "pb-25",
        "bm-predictive-group--separated"
      );
      group.style.marginTop = "";
      group.style.borderBottom = "";
      group.style.borderTop = "";

      if (index > 0) {
        group.classList.add("bm-predictive-group--separated");
      }
    });
  }

  mergeProductResults({
    resultsRoot,
    mappedProductResponses,
    mappedEntriesGroup,
    includeNativeProducts = true,
    sortAlphabetically = false,
    selectPopularBeforeAlphabeticalSort = false
  }) {
    const nativeProductsGroup = resultsRoot.querySelector(
      '[data-predictive-group="products"]'
    );

    const productCards = [];
    const seenProductKeys = new Set();
    const productSources = [];
    let sourceGrid = null;

    if (includeNativeProducts && nativeProductsGroup) {
      const nativeGrid = nativeProductsGroup.querySelector(".grid-cols");

      if (nativeGrid) {
        productSources.push({
          sourceGrid: nativeGrid,
          productRank: this.getProductResponseRank({
            searchTier: "native"
          }),
          sortAlphabetically: false,
          sourceIndex: productSources.length
        });
      }
    }

    (Array.isArray(mappedProductResponses)
      ? mappedProductResponses
      : []
    ).forEach((response) => {
      const mappedProducts = this.readMappedProducts(
        response?.text,
        response?.facetEntries,
        response?.productPredicate
      );

      const mappedGrid = mappedProducts?.querySelector(".grid-cols");

      if (!mappedGrid) {
        return;
      }

      productSources.push({
        sourceGrid: mappedGrid,
        productRank: this.getProductResponseRank(response),
        sortAlphabetically: response?.sortAlphabetically === true,
        sourceIndex: productSources.length
      });
    });

    productSources
      .sort((sourceA, sourceB) => {
        return (
          sourceA.productRank - sourceB.productRank ||
          sourceA.sourceIndex - sourceB.sourceIndex
        );
      })
      .forEach((source) => {
        sourceGrid ||= source.sourceGrid;

        this.addUniqueProductCards({
          sourceGrid: source.sourceGrid,
          productCards,
          seenProductKeys,
          sortAlphabetically: source.sortAlphabetically
        });
      });

    /*
     * Rene typesøgninger henter produkterne i popularitetsrækkefølge.
     * Vælg først de mest populære, og sorter derefter netop dette udvalg
     * alfabetisk til visningen. Ellers ville et alfabetisk sorteret udsnit
     * af hele responsen kunne skubbe mere populære produkter ud.
     */
    const selectedProductCards = selectPopularBeforeAlphabeticalSort
      ? productCards.slice(0, this.maximumPredictiveProducts)
      : [...productCards];

    if (sortAlphabetically) {
      selectedProductCards.sort((cardA, cardB) => {
        return this.getProductCardTitle(cardA).localeCompare(
          this.getProductCardTitle(cardB),
          this.isDanish() ? "da" : "en",
          {
            sensitivity: "base",
            numeric: true
          }
        );
      });
    }

    const limitedCards = selectedProductCards.slice(
      0,
      this.maximumPredictiveProducts
    );

    if (!limitedCards.length || !sourceGrid) {
      if (!includeNativeProducts) {
        nativeProductsGroup?.remove();
        return null;
      }

      return nativeProductsGroup || null;
    }

    const combinedGroup = document.createElement("div");
    combinedGroup.className = "search-product flex-1 pt-25";
    combinedGroup.setAttribute("data-predictive-group", "products");
    combinedGroup.setAttribute("data-esensera-combined-products", "");

    const combinedGrid = sourceGrid.cloneNode(false);

    limitedCards.forEach((card) => {
      combinedGrid.appendChild(card);
    });

    combinedGroup.appendChild(combinedGrid);

    if (nativeProductsGroup) {
      nativeProductsGroup.replaceWith(combinedGroup);
    } else if (mappedEntriesGroup) {
      mappedEntriesGroup.insertAdjacentElement("afterend", combinedGroup);
    } else {
      resultsRoot.insertBefore(combinedGroup, resultsRoot.firstChild);
    }

    return combinedGroup;
  }

  addUniqueProductCards({
    sourceGrid,
    productCards,
    seenProductKeys,
    sortAlphabetically = false
  }) {
    const cards = Array.from(sourceGrid.children);

    if (sortAlphabetically) {
      cards.sort((cardA, cardB) => {
        return this.getProductCardTitle(cardA).localeCompare(
          this.getProductCardTitle(cardB),
          this.isDanish() ? "da" : "en",
          {
            sensitivity: "base",
            numeric: true
          }
        );
      });
    }

    cards.forEach((card, index) => {
      const clonedCard = document.importNode(card, true);
      const productKey =
        this.getProductKey(clonedCard) ||
        `anonymous-${productCards.length}-${index}`;

      if (seenProductKeys.has(productKey)) {
        return;
      }

      seenProductKeys.add(productKey);
      productCards.push(clonedCard);
    });
  }

  getProductCardTitle(card) {
    const titleLink = card.querySelector?.(".product-name a");

    return String(
      titleLink?.textContent ||
      titleLink?.getAttribute("aria-label") ||
      ""
    ).trim();
  }

  getProductKey(card) {
    const productLink =
      card.matches?.('a[href*="/products/"]')
        ? card
        : card.querySelector?.('a[href*="/products/"]');

    const href = productLink?.getAttribute("href");

    if (href) {
      try {
        const url = new URL(href, window.location.origin);
        return url.pathname.replace(/\/$/, "");
      } catch (error) {
        return href.split("?")[0].replace(/\/$/, "");
      }
    }

    const productIdElement = card.querySelector?.("[data-product-id]");
    const productId = productIdElement?.getAttribute("data-product-id");

    return productId ? `product-${productId}` : null;
  }

  toValueArray(value) {
    if (Array.isArray(value)) {
      return value.flatMap((item) => this.toValueArray(item));
    }

    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      return [];
    }

    if (typeof value === "object" && "value" in value) {
      return this.toValueArray(value.value);
    }

    const stringValue = String(value);

    if (stringValue.includes(",")) {
      return stringValue
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [stringValue];
  }

  productMatchesFacetEntry(product, entry) {
    const paramName = String(entry?.filter_param || "").trim();
    const filterValue = this.normalizeTerm(entry?.filter_value);

    if (!paramName || !filterValue) {
      return true;
    }

    if (paramName === "filter.p.product_type") {
      const productType = this.normalizeTerm(product?.productType);
      const collectionHandles = this.toValueArray(
        product?.collectionHandles
      ).map((value) => this.normalizeTerm(value));
      const entryHandle = this.normalizeTerm(entry?.handle);

      return (
        productType === filterValue ||
        (entryHandle && collectionHandles.includes(entryHandle))
      );
    }

    const productFieldByParameter = {
      "filter.p.m.custom.scent_profile": "scentProfile",
      "filter.p.m.custom.perfumery_note": "perfumeryNote",
      "filter.p.m.custom.aromatherapy": "aromatherapy",
      "filter.p.m.custom.custom_product_badge": "customProductBadge",
      "filter.p.tag": "tags"
    };

    const productField = productFieldByParameter[paramName];

    if (!productField) {
      return true;
    }

    return this.toValueArray(product?.[productField])
      .map((value) => this.normalizeTerm(value))
      .includes(filterValue);
  }

  productMatchesFacetEntries(product, facetEntries) {
    return (Array.isArray(facetEntries) ? facetEntries : []).every((entry) => {
      return this.productMatchesFacetEntry(product, entry);
    });
  }

  readMappedProducts(
    text,
    facetEntries = [],
    productPredicate = null
  ) {
    if (!text) {
      return null;
    }

    const parsedHTML = new DOMParser().parseFromString(text, "text/html");

    const returnedProducts = parsedHTML.querySelector(
      "[data-esensera-predictive-products]"
    );

    if (
      !returnedProducts ||
      !returnedProducts.querySelector(".search-product")
    ) {
      return null;
    }

    if (
      (!Array.isArray(facetEntries) || !facetEntries.length) &&
      typeof productPredicate !== "function"
    ) {
      return returnedProducts;
    }

    const cardsGrid =
      returnedProducts.querySelector(
        "[data-esensera-predictive-product-cards]"
      ) || returnedProducts.querySelector(".grid-cols");

    const indexElement = returnedProducts.querySelector(
      "[data-esensera-predictive-product-index]"
    );

    if (!cardsGrid || !indexElement) {
      return null;
    }

    let productIndex = [];

    try {
      productIndex = JSON.parse(indexElement.textContent.trim() || "[]");
    } catch (error) {
      console.warn("Predictive produktindeks kunne ikke læses:", error);
      return null;
    }

    const cards = Array.from(cardsGrid.children);
    const filteredGrid = cardsGrid.cloneNode(false);

    productIndex.forEach((product, index) => {
      const card = cards[index];

      if (
        !card ||
        !this.productMatchesFacetEntries(product, facetEntries) ||
        (typeof productPredicate === "function" &&
          !productPredicate(product))
      ) {
        return;
      }

      filteredGrid.appendChild(document.importNode(card, true));
    });

    if (!filteredGrid.children.length) {
      return null;
    }

    const wrapper = document.createElement("div");
    wrapper.appendChild(filteredGrid);

    return wrapper;
  }

  insertContentGroups({ resultsRoot, contentMatches }) {
    const footer = resultsRoot.querySelector("[data-predictive-footer]");

    const insertGroup = (group) => {
      if (!group) return;

      if (footer) {
        resultsRoot.insertBefore(group, footer);
      } else {
        resultsRoot.appendChild(group);
      }
    };

    insertGroup(this.createContentGroup("page", contentMatches.pages));
    insertGroup(this.createContentGroup("article", contentMatches.articles));
  }

  createContentGroup(type, items) {
    if (!items.length) {
      return null;
    }

    const group = document.createElement("div");
    group.className = "pt-25 pb-25 border-top";
    group.setAttribute("data-predictive-group", `${type}s`);
    group.setAttribute("data-esensera-content-group", type);

    items.forEach((item) => {
      group.appendChild(this.createContentResult(item));
    });

    return group;
  }

  createContentResult(item) {
    const link = document.createElement("a");
    link.href = item.url;
    link.setAttribute("role", "option");
    link.setAttribute("aria-selected", "false");
    link.setAttribute("aria-label", item.title || "");

    if (item.type === "article") {
      const hasImage = Boolean(String(item.imageUrl || "").trim());
      link.className = [
        "quick-url",
        "predictive-search__item",
        "esensera-predictive-article",
        "mb-15",
        "last-0",
        hasImage ? "" : "esensera-predictive-article--no-image"
      ].filter(Boolean).join(" ");

      const copy = document.createElement("span");
      copy.className = "esensera-predictive-article__copy";

      const meta = document.createElement("span");
      meta.className = "esensera-predictive-kicker";
      meta.textContent = [
        this.getInterfaceText("Artikler", "Articles"),
        String(item.blogTitle || "").trim()
      ].filter(Boolean).join(" / ");
      copy.appendChild(meta);

      const title = document.createElement("span");
      title.className = "esensera-predictive-article__title heading-color";
      title.textContent = item.title || "";
      copy.appendChild(title);

      const excerpt = this.truncateText(item.excerptText, 170);

      if (excerpt) {
        const excerptElement = document.createElement("span");
        excerptElement.className = "esensera-predictive-article__excerpt fs-small";
        excerptElement.textContent = excerpt;
        copy.appendChild(excerptElement);
      }

      link.appendChild(copy);

      if (hasImage) {
        const media = document.createElement("span");
        media.className = "esensera-predictive-article__media";

        const image = document.createElement("img");
        image.src = item.imageUrl;
        image.alt = "";
        image.loading = "lazy";
        image.decoding = "async";
        media.appendChild(image);
        link.appendChild(media);
      }

      return link;
    }

    link.className =
      "quick-url d-block hover:underline mb-15 last-0 predictive-search__item";

    const title = document.createElement("span");
    title.className = "d-block heading-color";
    title.textContent = item.title || "";
    link.appendChild(title);

    const excerpt = this.truncateText(item.excerptText, 170);

    if (excerpt) {
      const excerptElement = document.createElement("span");
      excerptElement.className = "d-block fs-small mt-5";
      excerptElement.textContent = excerpt;
      link.appendChild(excerptElement);
    }

    return link;
  }

  truncateText(value, maximumLength) {
    const text = String(value || "").replace(/\s+/g, " ").trim();

    if (text.length <= maximumLength) {
      return text;
    }

    return `${text.slice(0, maximumLength).trimEnd()}…`;
  }

  removeDuplicateCollections(resultsRoot, matches) {
    const profileUrls = new Set(
      matches
        .filter(
          (entry) => entry.kind === "scent_profile" && entry.url
        )
        .map((entry) => this.normalizeComparableUrl(entry.url))
    );

    if (!profileUrls.size) {
      return;
    }

    const collectionGroup = resultsRoot.querySelector(
      '[data-predictive-group="collections"]'
    );

    if (!collectionGroup) {
      return;
    }

    collectionGroup
      .querySelectorAll("[data-predictive-collection]")
      .forEach((collectionLink) => {
        const collectionUrl = this.normalizeComparableUrl(
          collectionLink.getAttribute("href")
        );

        if (profileUrls.has(collectionUrl)) {
          collectionLink.remove();
        }
      });

    if (
      !collectionGroup.querySelector("[data-predictive-collection]")
    ) {
      collectionGroup.remove();
    }
  }

  normalizeComparableUrl(value) {
    if (!value) {
      return "";
    }

    try {
      const url = new URL(value, window.location.origin);
      const sortedParameters = [...url.searchParams.entries()].sort(
        ([keyA, valueA], [keyB, valueB]) => {
          const keyComparison = keyA.localeCompare(keyB);

          if (keyComparison !== 0) {
            return keyComparison;
          }

          return valueA.localeCompare(valueB);
        }
      );

      url.search = "";

      sortedParameters.forEach(([key, parameterValue]) => {
        url.searchParams.append(key, parameterValue);
      });

      return url.pathname.replace(/\/$/, "") + url.search;
    } catch (error) {
      return String(value || "");
    }
  }

  ensureMoreResultsFooter(resultsRoot, searchTerm) {
    let footer = resultsRoot.querySelector("[data-predictive-footer]");
    const searchUrl = this.resolveSearchDestination(searchTerm);

    if (footer) {
      const link = footer.querySelector(
        "[data-predictive-more-results]"
      );

      if (link) {
        link.href = searchUrl;
      }

      return;
    }

    footer = document.createElement("div");
    footer.className =
      "text-center bg-default inset-x-0 py-30 drawer__footer";
    footer.setAttribute("data-predictive-footer", "");

    const link = document.createElement("a");
    link.href = searchUrl;
    link.setAttribute("role", "option");
    link.setAttribute("aria-selected", "false");
    link.setAttribute(
      "aria-label",
      this.getInterfaceText("Se alle resultater", "View all results")
    );
    link.setAttribute("data-predictive-more-results", "");
    link.className =
      "predictive-search__item btn btn-see-all-search more-result-link align-self-center w-full";
    link.textContent = this.getInterfaceText(
      "Se alle resultater",
      "View all results"
    );

    footer.appendChild(link);
    resultsRoot.appendChild(footer);
  }

  isPureTypeIntent(queryContext) {
    const entries = Array.isArray(queryContext?.entries)
      ? queryContext.entries
      : [];

    return (
      queryContext?.analysis?.isIntentOnly === true &&
      entries.length === 1 &&
      entries[0]?.kind === "type"
    );
  }

  hasStrictOrganicTypeIntent(queryContext) {
    if (!queryContext?.analysis?.isIntentOnly) {
      return false;
    }

    const hasOrganic = queryContext.entries.some(
      (entry) => entry?.kind === "organic"
    );

    const hasType = queryContext.entries.some(
      (entry) => entry?.kind === "type"
    );

    return hasOrganic && hasType;
  }

  getCachedStrictProductCount(searchTerm) {
    const outcome = this.latestSearchOutcome;

    if (
      !outcome ||
      outcome.query !== this.normalizeTerm(searchTerm) ||
      !Number.isFinite(outcome.strictProductCount)
    ) {
      return null;
    }

    return outcome.strictProductCount;
  }

  async fetchStrictIntentProductCount(queryContext) {
    const requestUrl = this.buildFacetCollectionUrl(
      queryContext.entries,
      { sectionId: "esensera-predictive-products" }
    );

    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => controller.abort(),
      3000
    );

    try {
      const response = await fetch(requestUrl, {
        signal: controller.signal,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!response.ok) {
        return null;
      }

      const text = await response.text();

      return this.readProductIndexFromResponse(text).filter(
        (product) =>
          this.productMatchesFacetEntries(
            product,
            queryContext.entries
          )
      ).length;
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn(
          "Den strikse søgeintention kunne ikke kontrolleres:",
          error
        );
      }

      return null;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async resolveSearchDestinationForSubmit(searchTerm) {
    const directDestinationEntry =
      this.getDirectDestinationEntry(searchTerm);

    if (directDestinationEntry) {
      return directDestinationEntry.url;
    }

    /*
     * Submit-kontrakten er bevidst streng: kun et fuldt, eksakt term i
     * search map må ændre navigationen. Predictive præfikser og fuzzy
     * intent må gerne hjælpe forslag og produkt-rangering, men Enter/
     * "Se alle resultater" forbliver en fri søgning.
     */
    const directCollectionEntry =
      this.getExactCollectionDestinationEntry(searchTerm);

    if (directCollectionEntry) {
      return directCollectionEntry.url;
    }

    const exactFacetEntry =
      this.getExactQueryFacetEntry(searchTerm);

    if (exactFacetEntry) {
      return exactFacetEntry.url;
    }

    return this.buildFullSearchUrl(searchTerm);
  }

  shouldUseStrictEmptySearchPage(searchTerm, queryContext) {
    const outcome = this.latestSearchOutcome;

    if (
      !queryContext?.analysis?.isIntentOnly ||
      !outcome ||
      outcome.query !== this.normalizeTerm(searchTerm) ||
      outcome.strictProductCount !== 0
    ) {
      return false;
    }

    const hasOrganic = queryContext.entries.some(
      (entry) => entry?.kind === "organic"
    );

    const hasType = queryContext.entries.some(
      (entry) => entry?.kind === "type"
    );

    /*
     * En umulig, eksplicit kombination som "øko absolut" skal blive
     * en ærlig tom søgeresultatside. Duftkarakter-kombinationer bliver
     * derimod på Alle produkter med alle valgte tags synlige, også ved 0.
     */
    return hasOrganic && hasType;
  }

  resolveSearchDestination(searchTerm) {
    const directDestinationEntry =
      this.getDirectDestinationEntry(searchTerm);

    if (directDestinationEntry) {
      return directDestinationEntry.url;
    }

    const directCollectionEntry =
      this.getExactCollectionDestinationEntry(searchTerm);

    if (directCollectionEntry) {
      return directCollectionEntry.url;
    }

    const exactFacetEntry =
      this.getExactQueryFacetEntry(searchTerm);

    if (exactFacetEntry) {
      return exactFacetEntry.url;
    }

    return this.buildFullSearchUrl(searchTerm);
  }

  buildFullSearchUrl(
    searchTerm,
    context = null,
    { includePreferred = false } = {}
  ) {
    const url = new URL(
      this.getSearchRoute(),
      window.location.origin
    );

    url.searchParams.set("q", searchTerm);
    url.searchParams.set("options[prefix]", "last");
    url.searchParams.set(
      "options[unavailable_products]",
      "show"
    );
    url.searchParams.set("type", "all");
    url.searchParams.set(
      "options[fields]",
      "title,vendor,product_type,tag,variants.title,variants.sku"
    );

    return url.pathname + url.search;
  }

  getScentProfileColor(value) {
    const colors = {
      citrus: "rgba(243, 215, 30, 0.70)",
      floral: "rgba(205, 123, 177, 0.70)",
      aromatic: "rgba(79, 168, 48, 0.70)",
      fresh: "rgba(48, 163, 160, 0.70)",
      spicy: "rgba(202, 61, 40, 0.70)",
      gourmand: "rgba(218, 183, 119, 0.70)",
      resinous: "rgba(221, 145, 114, 0.70)",
      woody: "rgba(83, 51, 14, 0.70)"
    };

    const key = this.normalizeTerm(value);
    return colors[key] || null;
  }

  getGroupLabel(kind) {
    const group = this.searchMap?.groups?.[kind];

    if (!group) {
      return kind;
    }

    return this.isDanish()
      ? group.label_da || group.label_en || kind
      : group.label_en || group.label_da || kind;
  }

  getInterfaceText(danishText, englishText) {
    return this.isDanish() ? danishText : englishText;
  }

  isDanish() {
    return document.documentElement.lang
      .toLowerCase()
      .startsWith("da");
  }

  escapeHtml(value) {
    const element = document.createElement("div");
    element.textContent = String(value || "");
    return element.innerHTML;
  }

  close() {
    this.abortController?.abort();
    this.abortController = null;
    ++this.requestId;

    this.form?.classList.remove("loading");
    this.predictiveSearchResults?.classList.add("d-none");
    this.recommendSearch?.classList.remove("d-none");
    this.searchKeyword?.classList.remove("d-none");
    this.input?.setAttribute("aria-expanded", "false");

    if (!this.getQuery().length) {
      trapFocus(this.form);
    }
  }

  open() {
    trapFocus(this.form);

    this.predictiveSearchResults?.classList.remove("d-none");
    this.recommendSearch?.classList.add("d-none");
    this.searchKeyword?.classList.add("d-none");
    this.input?.setAttribute("aria-expanded", "true");
  }

  lazyloadImage() {
    const images = this.querySelectorAll(".loading-animation");

    images.forEach((element, index) => {
      const image = new Image();
      image.src = element.getAttribute("src");

      image.addEventListener("load", function () {
        setTimeout(() => {
          element.classList.add("loaded-animation");
          element.classList.remove("loading-animation");
        }, index * 100);
      });
    });
  }

  debounce(fn, wait) {
    let timeout;

    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}
